import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  ArrowUpRight, ArrowDownRight, ChevronRight, X, CheckCircle, TrendingUp, BarChart2, Trophy,
  Activity, CircleDollarSign, Cpu, Crosshair, Flame, Gauge, History,
  Radio, RefreshCw, ShieldCheck, WalletCards, CalendarDays,
} from "lucide-react";
import EquityCurveCanvas from "./EquityCurveCanvas";
import AnimatedNumber from "./AnimatedNumber";
import ProgressRing from "./ProgressRing";
import RegimeBanner from "./RegimeBanner";
import BotStatusStrip from "./BotStatusStrip";
import PnlHeatmap from "./PnlHeatmap";
import {
  WeeklySummaryCard, StreakDrawdownStrip, ActivityFeed,
  EngineLeaderboard, PortfolioRiskDonut, TradeQuickFilter,
} from "./TradingExtras";
import { marketGet } from "./marketApi";
import "../markets/markets.css";
import MarketsHeader from "../markets/MarketsHeader";
import LiveDesk from "../markets/LiveDesk";
import Performance from "../markets/Performance";
import RiskAccounts from "../markets/RiskAccounts";
import TradeLedger from "../markets/TradeLedger";
import { MarketsCtx } from "../markets/ui";
import { ACTIVE_SCOPE, count as fmtCount, dayKey, money, notional, pct as fmtPct, tone } from "../markets/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const APIKEY = process.env.REACT_APP_API_KEY || "";
const axCfg = { headers: { "X-API-Key": APIKEY } };

const HIDDEN_ACCOUNTS = new Set(["BENCHMARK", "THE_PROOF"]);

const ACCOUNT_META = {
  PRO:       { color: "#5cf2ff", role: "Pro Capital",  icon: "◇" },
  STARTER:   { color: "#4dffaf", role: "Starter Lab",  icon: "◈" },
  REAL_LIFE: { color: "#ffd76a", role: "Real Life",    icon: "◉" },
  PERSONAL:  { color: "#a78bfa", role: "Personal",     icon: "◆" },
  RESEARCH:  { color: "#c4b5fd", role: "Research",     icon: "◌" },
  LAB:       { color: "#4dffaf", role: "Research Lab", icon: "◎" },
  TIER_5K:   { color: "#ff5cb4", role: "Tier 5K",      icon: "✦" },
  TIER_1K:   { color: "#f5b042", role: "Tier 1K",      icon: "○" },
  TIER_500:  { color: "#5cf2ff", role: "Tier 500",     icon: "◊" },
};

const COIN_LOGOS = {
  BTC:  "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  ETH:  "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  SOL:  "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  BNB:  "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  ADA:  "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  LINK: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
  DOT:  "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
  AVAX: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  XRP:  "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  DOGE: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  UNI:  "https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png",
  LTC:  "https://assets.coingecko.com/coins/images/2/small/litecoin.png",
  SUI:  "https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg",
  NEAR: "https://assets.coingecko.com/coins/images/10365/small/near.jpg",
  POL:  "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png",
};

const ENGINE_THEMES = {
  autonomous_trader_v2:  { glyph: "⬡", color: "#00E5FF", label: "AUTO V2" },
  free_will_v2:          { glyph: "◈", color: "#FF6B35", label: "FREEWILL" },
  dual_engine:           { glyph: "◑", color: "#B377FF", label: "DUAL" },
  vwap_scalper:          { glyph: "◎", color: "#4DFF91", label: "VWAP" },
  yolo_engine:           { glyph: "⚡", color: "#FFD700", label: "YOLO" },
  elite_strategy:        { glyph: "◆", color: "#FF5CB4", label: "ELITE" },
  elite_strategy_v3:     { glyph: "◆", color: "#FF5CB4", label: "ELITE V3" },
  institutional_scalper: { glyph: "⊕", color: "#5CF2FF", label: "SCALPER" },
  tcn_neural:            { glyph: "⬢", color: "#4D6BFF", label: "TCN" },
  quant_analyzer:        { glyph: "∑", color: "#4DFFAF", label: "QUANT" },
  day_trader:            { glyph: "☀", color: "#FFB800", label: "DAY" },
};

const ENGINE_LABEL = {
  autonomous_trader_v2: "AUTO V2",
  free_will_v2: "FREEWILL",
  dual_engine: "DUAL",
  vwap_scalper: "VWAP",
  yolo_engine: "YOLO",
  elite_strategy: "ELITE",
  institutional_scalper: "SCALPER",
  tcn_neural: "TCN",
  quant_analyzer: "QUANT",
  day_trader: "DAY",
};

// timeframe → { bucketMs, windowMs, tickFmt }
const TF_CONFIG = {
  "5m":  { bucketMs: 5  * 60000, windowMs: 8  * 3600000, tickFmt: (t) => new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "15m": { bucketMs: 15 * 60000, windowMs: 24 * 3600000, tickFmt: (t) => new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "30m": { bucketMs: 30 * 60000, windowMs: 48 * 3600000, tickFmt: (t) => new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "1D":  { bucketMs: 24 * 3600000, windowMs: 7  * 24 * 3600000, tickFmt: (t) => new Date(t).toLocaleDateString([], { month: "short", day: "numeric" }) },
  "1W":  { bucketMs: 7  * 24 * 3600000, windowMs: 30 * 24 * 3600000, tickFmt: (t) => new Date(t).toLocaleDateString([], { month: "short", day: "numeric" }) },
};

// Unsigned for positives ("$12.00"), ASCII minus for negatives ("-$14.40");
// callers that prefix "+" get "+$0.21". Same shape as markets/format.money.
const fmtUsd = (v, dec = 2) => money(v, { dec, sign: false });

const fmtAge = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const fmtPrice = (v) => {
  const n = Number(v);
  if (!n) return "—";
  return n < 1 ? n.toFixed(5) : n < 10 ? n.toFixed(3) : n.toFixed(2);
};

const cleanReasonText = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value.replace(/_/g, " ").trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    return String(value.reason || value.name || value.label || value.message || "").replace(/_/g, " ").trim();
  }
  return "";
};

const firstReason = (...values) => values.map(cleanReasonText).find(Boolean) || "";

const listReasons = (...values) => values.flatMap((value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(cleanReasonText).filter(Boolean);
  return [cleanReasonText(value)].filter(Boolean);
});

// Aliases used by PositionCard / CallingCardModal (match HubCommandPage helpers)
const fmt = (p) => {
  if (p == null) return "—";
  const n = Number(p);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(n) >= 1)    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 });
};
const ago = fmtAge;

const accentFor = (account) => (ACCOUNT_META[account]?.color) || "#5cf2ff";
const labelFor = (strategy) =>
  ENGINE_LABEL[String(strategy || "").toLowerCase()] ||
  String(strategy || "").replace(/_/g, " ") || "—";
const themeFor = (strategy) =>
  ENGINE_THEMES[String(strategy || "").toLowerCase()] ||
  { glyph: "◇", color: "#5cf2ff", label: "TRADE" };

const WEEX_VALIDATION_META = {
  DEMO_VALIDATED: { label: "WEEX DEMO", color: "#4ade80", background: "rgba(74,222,128,0.10)" },
  MARKET_VALIDATED: { label: "WEEX DEPTH", color: "#4ad6ff", background: "rgba(74,214,255,0.10)" },
  DEMO_PENDING: { label: "DEMO PENDING", color: "#f5b042", background: "rgba(245,176,66,0.10)" },
  DEMO_DRIFT: { label: "DEMO DRIFT", color: "#ff8a94", background: "rgba(255,138,148,0.10)" },
  DEMO_REJECTED: { label: "DEMO REJECTED", color: "#ff5d6c", background: "rgba(255,93,108,0.10)" },
  DRIFT: { label: "WEEX DRIFT", color: "#f5b042", background: "rgba(245,176,66,0.10)" },
  UNSUPPORTED: { label: "NO WEEX", color: "#94a3b8", background: "rgba(148,163,184,0.09)" },
  WEEX_UNAVAILABLE: { label: "WEEX OFFLINE", color: "#ff8a94", background: "rgba(255,138,148,0.09)" },
};

const weexMetaFor = (position) => {
  const validation = position?.weex_validation || {};
  const status = String(validation.status || validation.market_status || "SYNCING").toUpperCase();
  const meta = WEEX_VALIDATION_META[status] || {
    label: "WEEX SYNC",
    color: "#94a3b8",
    background: "rgba(148,163,184,0.09)",
  };
  return {
    ...meta,
    status,
    validation,
    reason: validation.market_reason || validation.demo_error || "Waiting for the WEEX validation worker.",
  };
};

// ── Build time-bucketed data from equity curve ─────────────────────────────
function buildBuckets(equity, tf) {
  const { bucketMs, windowMs } = TF_CONFIG[tf];
  const now = Date.now();
  const cutoff = now - windowMs;
  const filtered = equity.filter(e => new Date(e.date).getTime() >= cutoff);
  if (!filtered.length) return [];
  const map = new Map();
  for (const e of filtered) {
    const t = new Date(e.date).getTime();
    const key = Math.floor(t / bucketMs) * bucketMs;
    if (!map.has(key)) map.set(key, 0);
    map.set(key, map.get(key) + (e.pnl || 0));
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([time, pnl]) => ({ time, pnl }));
}

function buildEquityWindow(equity, tf) {
  const { windowMs } = TF_CONFIG[tf];
  const cutoff = Date.now() - windowMs;
  const filtered = equity.filter(e => new Date(e.date).getTime() >= cutoff);
  let cum = 0;
  return filtered.map((e, i) => ({ x: i, y: (cum += (e.pnl || 0)), date: e.date }));
}

// ── PnL bar chart (canvas) ─────────────────────────────────────────────────
function PnlBarCanvas({ buckets, height = 160 }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const draw = (progress) => {
      const dpr = window.devicePixelRatio || 1;
      const w = wrap.clientWidth;
      const h = height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      if (!buckets || buckets.length < 1) {
        ctx.fillStyle = "rgba(255,255,255,0.3)";
        ctx.font = "11px ui-sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("collecting pnl data…", w / 2, h / 2);
        return;
      }

      const vals = buckets.map(b => b.pnl);
      const maxAbs = Math.max(Math.abs(Math.max(...vals)), Math.abs(Math.min(...vals)), 0.01);
      const padL = 36, padR = 8, padT = 14, padB = 22;
      const innerW = w - padL - padR;
      const innerH = h - padT - padB;
      const zeroY = padT + innerH / 2;

      // Grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const y = padT + (innerH / 4) * i;
        ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(w - padR, y); ctx.stroke();
      }

      // Axis labels
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "9px ui-monospace,monospace";
      ctx.textAlign = "right";
      const fmt = v => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0);
      ctx.fillText(`+${fmt(maxAbs)}`, padL - 4, padT + 4);
      ctx.fillText("0", padL - 4, zeroY + 3);
      ctx.fillText(`-${fmt(maxAbs)}`, padL - 4, padT + innerH);

      // Zero line
      ctx.strokeStyle = "rgba(255,255,255,0.14)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(padL, zeroY); ctx.lineTo(w - padR, zeroY); ctx.stroke();

      // Bars
      const barW = Math.max(2, (innerW / buckets.length) - 2);
      const xStep = innerW / buckets.length;
      buckets.forEach((b, i) => {
        const ratio = Math.min(1, Math.abs(b.pnl) / maxAbs) * progress;
        const barH = ratio * (innerH / 2);
        const x = padL + i * xStep + (xStep - barW) / 2;
        const isPos = b.pnl >= 0;
        const y = isPos ? zeroY - barH : zeroY;

        const grad = ctx.createLinearGradient(0, y, 0, y + barH);
        if (isPos) {
          grad.addColorStop(0, "rgba(74,222,128,0.9)");
          grad.addColorStop(1, "rgba(74,222,128,0.25)");
        } else {
          grad.addColorStop(0, "rgba(255,93,108,0.25)");
          grad.addColorStop(1, "rgba(255,93,108,0.9)");
        }
        ctx.fillStyle = grad;
        ctx.shadowColor = isPos ? "#4ade80" : "#ff5d6c";
        ctx.shadowBlur = barH > 4 ? 6 : 0;
        const r = Math.min(3, barW / 2);
        ctx.beginPath();
        ctx.roundRect(x, y, barW, Math.max(1, barH), r);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // X-axis tick labels (only a few)
      if (buckets.length > 0) {
        ctx.fillStyle = "rgba(255,255,255,0.28)";
        ctx.font = "8px ui-monospace,monospace";
        ctx.textAlign = "center";
        const maxTicks = Math.min(5, buckets.length);
        const step = Math.floor(buckets.length / maxTicks);
        for (let i = 0; i < buckets.length; i += step) {
          const x = padL + i * xStep + xStep / 2;
          ctx.fillText(TF_CONFIG["1D"] ? new Date(buckets[i].time).toLocaleDateString([], { month: "short", day: "numeric" }) : new Date(buckets[i].time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), x, h - 4);
        }
      }
    };

    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 700);
      const eased = 1 - Math.pow(1 - t, 3);
      draw(eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    const onResize = () => draw(1);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(rafRef.current); };
  }, [buckets, height]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <canvas ref={canvasRef} className="block w-full" />
    </div>
  );
}

// ── Live position chart canvas ─────────────────────────────────────────────
function LivePriceCanvas({ priceHistory, entry, sl, tp, livePnlPct, height = 120 }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const w = wrap.clientWidth;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (!priceHistory.length) return;

    const prices = priceHistory.map(p => p.p);
    const refs = [entry, sl > 0 ? sl : null, tp > 0 ? tp : null].filter(Boolean);
    const allP = [...prices, ...refs];
    const minP = Math.min(...allP) * 0.9994;
    const maxP = Math.max(...allP) * 1.0006;
    const span = maxP - minP || 1;
    const padX = 6, padY = 10;
    const innerW = w - padX * 2;
    const innerH = h - padY * 2;
    const toY = p => padY + innerH * (1 - (p - minP) / span);
    const toX = i => padX + (i / Math.max(1, priceHistory.length - 1)) * innerW;

    ctx.setLineDash([3, 3]);

    // Entry line
    ctx.strokeStyle = "rgba(255,255,255,0.25)"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(padX, toY(entry)); ctx.lineTo(w - padX, toY(entry)); ctx.stroke();

    // SL
    if (sl > 0) {
      ctx.strokeStyle = "rgba(255,93,108,0.45)";
      ctx.beginPath(); ctx.moveTo(padX, toY(sl)); ctx.lineTo(w - padX, toY(sl)); ctx.stroke();
      ctx.fillStyle = "rgba(255,93,108,0.5)"; ctx.font = "8px ui-monospace,monospace";
      ctx.textAlign = "right"; ctx.fillText("SL", w - padX - 2, toY(sl) - 3);
    }

    // TP
    if (tp > 0) {
      ctx.strokeStyle = "rgba(74,222,128,0.45)";
      ctx.beginPath(); ctx.moveTo(padX, toY(tp)); ctx.lineTo(w - padX, toY(tp)); ctx.stroke();
      ctx.fillStyle = "rgba(74,222,128,0.5)"; ctx.font = "8px ui-monospace,monospace";
      ctx.textAlign = "right"; ctx.fillText("TP", w - padX - 2, toY(tp) - 3);
    }

    ctx.setLineDash([]);

    if (priceHistory.length >= 2) {
      const lineColor = livePnlPct >= 0 ? "#4ade80" : "#ff5d6c";
      const grad = ctx.createLinearGradient(0, padY, 0, h);
      grad.addColorStop(0, livePnlPct >= 0 ? "rgba(74,222,128,0.22)" : "rgba(255,93,108,0.22)");
      grad.addColorStop(1, "rgba(0,0,0,0)");

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(toX(0), toY(priceHistory[0].p));
      priceHistory.forEach((pt, i) => ctx.lineTo(toX(i), toY(pt.p)));
      ctx.lineTo(toX(priceHistory.length - 1), h);
      ctx.lineTo(toX(0), h);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = lineColor; ctx.lineWidth = 2;
      ctx.shadowColor = lineColor; ctx.shadowBlur = 10;
      ctx.beginPath();
      priceHistory.forEach((pt, i) => {
        const x = toX(i), y = toY(pt.p);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke(); ctx.shadowBlur = 0;

      const lx = toX(priceHistory.length - 1);
      const ly = toY(priceHistory[priceHistory.length - 1].p);
      ctx.fillStyle = lineColor; ctx.shadowColor = lineColor; ctx.shadowBlur = 20;
      ctx.beginPath(); ctx.arc(lx, ly, 5, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(lx, ly, 2, 0, Math.PI * 2); ctx.fill();
    }
  }, [priceHistory, entry, sl, tp, livePnlPct, height]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <canvas ref={canvasRef} className="block w-full" />
    </div>
  );
}

// ── Synthetic mini-chart for closed trade modal ────────────────────────────
function TradeMiniChart({ trade, height = 88 }) {
  const entry = Number(trade.entry_price || 0);
  const exit  = Number(trade.exit_price || trade.current_price || entry);
  const sl    = Number(trade.stop_loss || trade.stop_price || 0);
  const tp    = Number(trade.take_profit || trade.target_price || 0);
  if (!entry) return null;
  const isLong = String(trade.direction || "").toUpperCase() === "LONG";
  const isWin  = exit > entry ? isLong : !isLong;
  const prices = [entry, exit, sl || entry, tp || entry].filter(Boolean);
  const minP = Math.min(...prices) * 0.998, maxP = Math.max(...prices) * 1.002;
  const span = maxP - minP || 1;
  const W = 260, H = height;
  const toY = (p) => H - ((Number(p) - minP) / span) * (H - 10) - 5;
  const x0 = 16, xM = W / 2, xE = W - 16;
  const y0 = toY(entry), yE = toY(exit);
  const midBump = isWin ? -14 : 14;
  const yM = (y0 + yE) / 2 + midBump;
  const curve = `M ${x0} ${y0} C ${xM - 40} ${yM - 6}, ${xM + 40} ${yM + 6}, ${xE} ${yE}`;
  const fill  = `${curve} L ${xE} ${H} L ${x0} ${H} Z`;
  const lineC = isWin ? "#4ade80" : "#ff5d6c";
  const fillC = isWin ? "rgba(74,222,128,0.1)" : "rgba(255,93,108,0.1)";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      {sl > 0 && <line x1={x0} y1={toY(sl)} x2={xE} y2={toY(sl)} stroke="rgba(255,93,108,0.35)" strokeWidth="1" strokeDasharray="4,3" />}
      {tp > 0 && <line x1={x0} y1={toY(tp)} x2={xE} y2={toY(tp)} stroke="rgba(74,222,128,0.35)" strokeWidth="1" strokeDasharray="4,3" />}
      {sl > 0 && <text x={xE - 3} y={toY(sl) - 3} textAnchor="end" fontSize="7" fill="rgba(255,93,108,0.6)">SL</text>}
      {tp > 0 && <text x={xE - 3} y={toY(tp) - 3} textAnchor="end" fontSize="7" fill="rgba(74,222,128,0.6)">TP</text>}
      <path d={fill} fill={fillC} />
      <path d={curve} fill="none" stroke={lineC} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx={x0} cy={y0} r="3.5" fill={lineC} opacity="0.7" />
      <circle cx={xE} cy={yE} r="4" fill={lineC} />
      <circle cx={xE} cy={yE} r="2" fill="white" />
    </svg>
  );
}

// ── Closed trade detail modal ──────────────────────────────────────────────
function TradeModal({ trade, isOpen, onClose }) {
  if (!trade) return null;
  const dir     = String(trade.direction || "").toUpperCase();
  const isLong  = dir === "LONG";
  const symbol  = String(trade.symbol || "").replace("/USDT", "");
  const pnlUsd  = Number(trade.pnl_usd ?? trade.realized_pnl ?? trade.pnl ?? 0);
  const pnlPct  = Number(trade.pnl_pct ?? 0);
  const account = trade.account_id || trade.account || "—";
  const engine  = labelFor(trade.strategy || trade.engine);
  const conf    = Number(trade.confidence || 0);
  const confs   = Array.isArray(trade.confirmations) ? trade.confirmations : [];
  const accent  = accentFor(account);
  const isWin   = pnlUsd >= 0;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/72" style={{ backdropFilter: "blur(18px)" }} />
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(14,18,42,0.98) 0%, rgba(6,8,18,0.98) 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 48px 96px -24px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.07)",
          animation: "arenaPopIn 300ms cubic-bezier(0.34,1.56,0.64,1) both",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent 0%, ${accent} 40%, ${accent} 60%, transparent 100%)`, opacity: 0.9 }} />
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="inline-grid place-items-center w-10 h-10 rounded-2xl"
                style={{ background: isLong ? "rgba(74,222,128,0.14)" : "rgba(255,93,108,0.14)", color: isLong ? "#4ade80" : "#ff5d6c" }}>
                {isLong ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />}
              </span>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-xl font-semibold text-white">{symbol}/USDT</span>
                  <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
                    style={{ background: isLong ? "rgba(74,222,128,0.14)" : "rgba(255,93,108,0.14)", color: isLong ? "#4ade80" : "#ff5d6c" }}>
                    {dir}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">{engine} · {account}</div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white/35 hover:text-white/75 hover:bg-white/10 transition">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 rounded-2xl"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-white/40 mb-1">Profit / Loss</div>
              <div className={`font-display text-2xl font-semibold ${isWin ? "text-emerald-300" : "text-rose-300"}`}>
                {pnlUsd >= 0 ? "+" : ""}{fmtUsd(pnlUsd)}
              </div>
            </div>
            <div className={`text-right ${isWin ? "text-emerald-400" : "text-rose-400"}`}>
              <div className="font-mono text-base font-bold">{pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/35 mt-0.5">return</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <InfoCell label="Entry"     value={fmtPrice(trade.entry_price)} />
            <InfoCell label={isOpen ? "Current" : "Exit"} value={fmtPrice(trade.exit_price || trade.current_price)} />
            {(trade.stop_loss || trade.stop_price)   ? <InfoCell label="Stop Loss"   value={fmtPrice(trade.stop_loss  || trade.stop_price)}   tone="rose" /> : null}
            {(trade.take_profit || trade.target_price) ? <InfoCell label="Take Profit" value={fmtPrice(trade.take_profit || trade.target_price)} tone="emerald" /> : null}
            {trade.leverage ? <InfoCell label="Leverage"    value={`${trade.leverage}x`} /> : null}
            {conf > 0       ? <InfoCell label="Confidence"  value={`${conf}%`} tone="cyan" /> : null}
            {trade.margin   ? <InfoCell label="Margin"      value={fmtUsd(trade.margin)} /> : null}
            {(trade.close_reason || trade.exit_reason) ? (
              <InfoCell label="Exit" value={(trade.close_reason || trade.exit_reason).replace(/_/g, " ")} tone="amber" />
            ) : null}
          </div>
          {confs.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-white/40 mb-2">Signal confirmations</div>
              <div className="flex flex-wrap gap-1.5">
                {confs.map((c, i) => (
                  <span key={i} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] text-cyan-200"
                    style={{ background: "rgba(74,214,255,0.1)", border: "1px solid rgba(74,214,255,0.22)" }}>
                    <CheckCircle className="h-3 w-3" /> {c}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-2xl overflow-hidden"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="px-3 pt-3 pb-0.5 flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-[0.24em] text-white/40">Price path</span>
              <span className="text-[9px] text-white/30 font-mono">{fmtAge(trade.entry_time || trade.opened_at)} ago</span>
            </div>
            <TradeMiniChart trade={trade} height={88} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Open position modal (live, animated) ──────────────────────────────────
function OpenPositionModal({ trade, onClose }) {
  const engine  = String(trade.strategy || trade.engine || "").toLowerCase();
  const theme   = themeFor(engine);
  const dir     = String(trade.direction || "").toUpperCase();
  const isLong  = dir === "LONG";
  const accent  = isLong ? "#4ade80" : "#ff5d6c";
  const symbol  = String(trade.symbol || "").replace("/USDT", "");
  const entry   = Number(trade.entry_price || 0);
  const sl      = Number(trade.stop_loss || trade.stop_price || 0);
  const tp      = Number(trade.take_profit || trade.target_price || 0);
  const lev     = Number(trade.leverage || 1);
  const confs   = Array.isArray(trade.confirmations) ? trade.confirmations : [];

  const [livePrice, setLivePrice] = useState(Number(trade.current_price || entry));
  const [priceHistory, setPriceHistory] = useState(() => [{ t: Date.now(), p: Number(trade.current_price || entry) }]);
  const [flash, setFlash] = useState(null);
  const prevPriceRef = useRef(Number(trade.current_price || entry));

  const livePnlPct = entry > 0
    ? (isLong ? (livePrice - entry) / entry : (entry - livePrice) / entry) * 100 * Math.max(1, lev)
    : 0;

  useEffect(() => {
    const tradeId = trade.id || trade._id || trade.trade_id;
    const poll = async () => {
      try {
        const res = await marketGet(`${API}/positions`, axCfg);
        const trades = res.data?.positions || (Array.isArray(res.data) ? res.data : []);
        const live = trades.find(t =>
          (tradeId && (t.id === tradeId || t._id === tradeId || String(t._id) === String(tradeId))) ||
          (t.symbol === trade.symbol && (t.account_id || t.account) === (trade.account_id || trade.account))
        );
        if (live) {
          const p = Number(live.current_price || live.entry_price || 0);
          if (p > 0) {
            const prev = prevPriceRef.current;
            if (prev > 0 && p !== prev) {
              setFlash(p > prev ? "up" : "down");
              setTimeout(() => setFlash(null), 500);
            }
            prevPriceRef.current = p;
            setLivePrice(p);
            setPriceHistory(h => [...h.slice(-80), { t: Date.now(), p }]);
          }
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade.id, trade._id, trade.trade_id, trade.symbol, trade.account_id, trade.account]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ animation: "aeonFadeIn 180ms ease both" }}
      onClick={onClose}>
      <div className="absolute inset-0 bg-black/82" style={{ backdropFilter: "blur(28px)" }} />

      <div
        className="relative w-full max-w-sm"
        style={{ animation: "openPositionIn 380ms cubic-bezier(0.34,1.56,0.64,1) both" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Outer glow ring */}
        <div className="absolute -inset-[1px] rounded-3xl"
          style={{
            background: `linear-gradient(135deg, ${theme.color}50, ${accent}40, ${theme.color}30)`,
            animation: "posGlowPulse 2.4s ease-in-out infinite",
          }} />

        {/* Card */}
        <div className="relative rounded-3xl overflow-hidden"
          style={{
            background: "linear-gradient(160deg, rgba(6,10,28,0.99) 0%, rgba(3,5,14,0.99) 100%)",
            border: `1px solid ${theme.color}35`,
            boxShadow: `0 0 60px ${theme.color}18, 0 0 20px ${accent}12, 0 48px 80px rgba(0,0,0,0.95)`,
          }}>

          {/* Top gradient bar */}
          <div style={{ height: 3, background: `linear-gradient(90deg, transparent, ${theme.color}, ${accent}, ${theme.color}, transparent)`, animation: "posBarSlide 3s linear infinite" }} />

          {/* Engine header */}
          <div className="px-4 pt-4 pb-3 flex items-center justify-between"
            style={{ borderBottom: `1px solid ${theme.color}12` }}>
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-bold select-none"
                style={{
                  background: `${theme.color}12`,
                  border: `1px solid ${theme.color}30`,
                  color: theme.color,
                  textShadow: `0 0 16px ${theme.color}`,
                  animation: "engineGlyphFloat 3.5s ease-in-out infinite",
                }}
              >
                {theme.glyph}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold uppercase tracking-[0.2em]" style={{ color: theme.color }}>
                    {theme.label}
                  </span>
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase"
                    style={{ background: "rgba(255,184,0,0.1)", color: "#FFB800", border: "1px solid rgba(255,184,0,0.28)" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
                    LIVE
                  </span>
                </div>
                <div className="text-[10px] text-white/40 mt-0.5 font-mono">
                  {symbol}/USDT · {trade.account_id || trade.account || "—"}
                </div>
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Direction + live PnL */}
          <div className="px-4 py-3 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-grid place-items-center w-8 h-8 rounded-xl"
                  style={{ background: isLong ? "rgba(74,222,128,0.14)" : "rgba(255,93,108,0.14)", color: accent }}>
                  {isLong ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                </span>
                <span className="font-display text-lg font-bold text-white">{dir}</span>
                {lev > 1 && <span className="text-xs font-mono text-white/38">{lev}x</span>}
              </div>
              <div className="text-[10px] text-white/35 font-mono">{fmtAge(trade.entry_time || trade.opened_at || trade.created_at)} open</div>
            </div>
            <div className="text-right">
              <div
                className={`font-display text-2xl font-bold ${
                  flash === "up" ? "text-emerald-300" : flash === "down" ? "text-rose-300" : livePnlPct >= 0 ? "text-emerald-300" : "text-rose-300"
                }`}
                style={{ transition: "color 250ms, transform 250ms", transform: flash ? "scale(1.06)" : "scale(1)" }}
              >
                {livePnlPct >= 0 ? "+" : ""}{livePnlPct.toFixed(2)}%
              </div>
              <div
                className={`font-mono text-xs mt-0.5 ${livePnlPct >= 0 ? "text-emerald-400/60" : "text-rose-400/60"}`}
              >
                live pnl
              </div>
            </div>
          </div>

          {/* Live chart */}
          <div className="mx-4 rounded-2xl overflow-hidden"
            style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-[0.26em] text-white/35">Live price</span>
              <span
                className={`font-mono text-sm font-bold transition-colors duration-200 ${
                  flash === "up" ? "text-emerald-300" : flash === "down" ? "text-rose-300" : "text-white"
                }`}
              >
                {fmtPrice(livePrice)}
              </span>
            </div>
            <LivePriceCanvas
              priceHistory={priceHistory}
              entry={entry} sl={sl} tp={tp}
              livePnlPct={livePnlPct}
              height={110}
            />
          </div>

          {/* Price levels */}
          <div className="px-4 py-3 grid grid-cols-3 gap-2">
            <PriceLevel label="Entry"  value={fmtPrice(entry)}  color="white" />
            <PriceLevel label="Stop"   value={sl  > 0 ? fmtPrice(sl)  : "—"} color="rose" />
            <PriceLevel label="Target" value={tp  > 0 ? fmtPrice(tp)  : "—"} color="emerald" />
          </div>

          {/* Confirmations */}
          {confs.length > 0 && (
            <div className="px-4 pb-4">
              <div className="flex flex-wrap gap-1.5">
                {confs.slice(0, 5).map((c, i) => (
                  <span key={i} className="rounded-full px-2 py-0.5 text-[9px]"
                    style={{ background: `${theme.color}10`, color: theme.color, border: `1px solid ${theme.color}22` }}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceLevel({ label, value, color }) {
  const clr = { white: "text-white", rose: "text-rose-300", emerald: "text-emerald-300", cyan: "text-cyan-300" }[color] || "text-white";
  const bd  = { white: "rgba(255,255,255,0.09)", rose: "rgba(255,93,108,0.2)", emerald: "rgba(74,222,128,0.2)", cyan: "rgba(74,214,255,0.2)" }[color] || "rgba(255,255,255,0.09)";
  return (
    <div className="rounded-xl p-2" style={{ border: `1px solid ${bd}`, background: "rgba(255,255,255,0.03)" }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/38 mb-0.5">{label}</div>
      <div className={`font-mono text-xs font-semibold ${clr}`}>{value || "—"}</div>
    </div>
  );
}

function InfoCell({ label, value, tone = "white" }) {
  const textColor = { white: "text-white", rose: "text-rose-300", emerald: "text-emerald-300", cyan: "text-cyan-300", amber: "text-amber-300" }[tone] || "text-white";
  return (
    <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/38 mb-0.5">{label}</div>
      <div className={`font-mono text-xs font-semibold ${textColor}`}>{value || "—"}</div>
    </div>
  );
}

// ── Live position calling card (matches Hub style exactly) ────────────────
function PositionCard({ pos, onClick }) {
  const sym    = pos.symbol?.replace("/USDT","") || "";
  const isLong = pos.direction === "LONG";
  const profit = (pos.unrealized_pnl ?? 0) >= 0;
  const logo   = COIN_LOGOS[sym];
  const pnlPct = Number(pos.unrealized_pnl_pct ?? pos.roe_pct ?? pos.pnl_pct ?? 0);
  const accountIcon = pos.account_emoji || ACCOUNT_META[pos.account_id]?.icon || "◇";
  const sc     = isLong ? "#4ade80" : "#ff5d6c";
  const weex   = weexMetaFor(pos);
  return (
    <div className="pos-card" onClick={onClick}>
      <div className="pos-card-side" style={{ background:sc }} />
      <div className="pos-card-body">
        <div className="pos-card-top">
          <div className="pos-card-coin">
            {logo ? <img src={logo} alt={sym} width={16} height={16} style={{ borderRadius:"50%", flexShrink:0 }} />
                  : <span className="pos-card-coin-abbr">{sym.slice(0,2)}</span>}
            <span className="pos-card-sym">{sym}</span>
            <span className="pos-card-dir" style={{ color:sc }}>{pos.direction}</span>
            <span className="pos-card-lev">{pos.leverage}x</span>
          </div>
          <div className="pos-card-pnl" style={{ color:profit?sc:"#ff5d6c" }}>
            {profit?"+":""}{pnlPct.toFixed(2)}%
          </div>
        </div>
        <div className="pos-card-bot">
          <span className="pos-card-acct" style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
            {accountIcon} {pos.account_id}
            <span
              title={weex.reason}
              style={{
                color:weex.color,
                background:weex.background,
                border:`1px solid ${weex.color}35`,
                borderRadius:999,
                padding:"1px 5px",
                fontSize:7,
                fontWeight:800,
                letterSpacing:"0.08em",
                whiteSpace:"nowrap",
              }}
            >
              {weex.label}
            </span>
          </span>
          <span className="pos-card-usd" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
            {profit?"+":""}${fmt(pos.unrealized_pnl)}
          </span>
        </div>
      </div>
      <ChevronRight size={13} style={{ color:"rgba(255,255,255,0.2)", flexShrink:0 }} />
    </div>
  );
}

// ── Calling card detail modal (matches Hub style exactly) ──────────────────
function CallingCardModal({ pos, onClose, onClosePosition, actionError }) {
  if (!pos) return null;
  const sym    = pos.symbol?.replace("/USDT","") || "";
  const isLong = pos.direction === "LONG";
  const pnl    = pos.unrealized_pnl ?? 0;
  const pnlPct = Number(pos.unrealized_pnl_pct ?? pos.roe_pct ?? pos.pnl_pct ?? 0);
  const profit = pnl >= 0;
  const logo   = COIN_LOGOS[sym];
  const strategy = pos.strategy || pos.engine || "unknown";
  const accountIcon = pos.account_emoji || ACCOUNT_META[pos.account_id]?.icon || "◇";
  const positionSize = pos.position_size_usd ?? pos.position_size ?? pos.notional ?? pos.size_usd;
  const range  = Math.abs((pos.take_profit||0) - (pos.stop_loss||0));
  const progress = range > 0
    ? Math.max(0, Math.min(100, (
        isLong
          ? (pos.current_price - pos.stop_loss) / range
          : (pos.stop_loss - pos.current_price) / range
      ) * 100))
    : 50;
  const ec     = ENGINE_THEMES[String(strategy).toLowerCase()]
    ? { bg:`${ENGINE_THEMES[String(strategy).toLowerCase()].color}12`, border:`${ENGINE_THEMES[String(strategy).toLowerCase()].color}40`, text:ENGINE_THEMES[String(strategy).toLowerCase()].color }
    : { bg:"rgba(255,255,255,0.06)", border:"rgba(255,255,255,0.15)", text:"rgba(255,255,255,0.5)" };
  const sc     = isLong ? "#4ade80" : "#ff5d6c";
  const sbg    = isLong ? "rgba(74,222,128,0.1)" : "rgba(255,93,108,0.1)";
  const weex   = weexMetaFor(pos);
  const validation = weex.validation;
  const signal  = pos.signal_data || {};
  const confirmations = listReasons(signal.confirmations, pos.confirmations);
  const confidence = firstReason(signal.confidence, pos.confidence) || "?";
  const regime = firstReason(
    signal.regime,
    signal.market_regime,
    signal.regime_context,
    pos.regime,
    pos.market_regime,
    pos.entry_regime
  ) || "Regime context not captured";
  const thesis = firstReason(
    signal.thesis,
    signal.entry_thesis,
    signal.reason,
    signal.entry_reason,
    signal.summary,
    pos.thesis,
    pos.entry_reason,
    pos.reason
  ) || `${labelFor(strategy)} opened a ${pos.direction || "directional"} setup on ${sym || "this market"} with ${confidence}% confidence.`;
  const invalidation = firstReason(
    signal.invalidation,
    signal.invalid_if,
    signal.exit_plan,
    pos.invalidation,
    pos.invalid_if
  ) || `${pos.direction || "Trade"} thesis weakens if price loses the stop area near $${fmt(pos.stop_loss || pos.stop_price)} or confirmations no longer agree.`;
  const riskPlan = [
    `Entry $${fmt(pos.entry_price)}`,
    `Stop $${fmt(pos.stop_loss || pos.stop_price)}`,
    `Target $${fmt(pos.take_profit || pos.target_price)}`,
    pos.liquidation_price ? `Liq $${fmt(pos.liquidation_price)}` : null,
    positionSize ? `Size $${fmt(positionSize)}` : null,
  ].filter(Boolean).join(" / ");
  const reasonRows = [
    { label: "Entry Thesis", value: thesis, tone: sc },
    { label: "Regime Context", value: regime, tone: "#4ad6ff" },
    { label: "Risk Plan", value: riskPlan, tone: "#f5b042" },
    { label: "Invalidation", value: invalidation, tone: "#ff8a94" },
    { label: "Engine Source", value: `${labelFor(strategy)}${pos.account_id ? ` / ${pos.account_id}` : ""}`, tone: ec.text },
  ];
  return (
    <div className="xcard-overlay" onClick={onClose}>
      <div className="xcard-shell" onClick={e => e.stopPropagation()}>
        <div className="xcard-hbar" style={{ background:`linear-gradient(135deg,${sbg},transparent)` }}>
          <div className="xcard-hbar-left">
            <div className="xcard-coin-ring" style={{ borderColor:sc }}>
              {logo ? <img src={logo} alt={sym} width={28} height={28} style={{ borderRadius:"50%" }} />
                    : <span style={{ fontSize:12,fontWeight:800,color:"#fff" }}>{sym.slice(0,2)}</span>}
            </div>
            <div>
              <div className="xcard-pair">{sym}<span style={{ color:"var(--text-mute)" }}>/USDT</span></div>
              <div className="xcard-meta-row">
                <span className="xcard-side-badge" style={{ background:sbg, color:sc, borderColor:sc }}>{pos.direction}</span>
                <span className="xcard-lev-badge">{pos.leverage}x</span>
                <span className="xcard-perp-badge">PERP</span>
              </div>
            </div>
          </div>
          <div className="xcard-hbar-right">
            <div className="xcard-live-row">
              <span className="xcard-live-dot" style={{ background:sc }} />
              <span className="xcard-live-txt">LIVE</span>
            </div>
            <button type="button" className="xcard-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="xcard-pnl-block" style={{ borderColor: profit?"rgba(74,222,128,0.2)":"rgba(255,93,108,0.2)" }}>
          <div className="xcard-pnl-left">
            <p className="xcard-pnl-label">Unrealized PnL</p>
            <p className="xcard-pnl-big" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
              {profit?"+":""}{pnlPct.toFixed(2)}%
            </p>
            <p className="xcard-pnl-usd" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
              {money(pnl)}
            </p>
          </div>
          <div className="xcard-rr-block">
            {Math.abs(pnlPct) > 30 && <Flame size={18} style={{ color:profit?"#4ade80":"#ff5d6c", marginBottom:4 }} />}
            <p className="xcard-rr-label">R:R Ratio</p>
            <p className="xcard-rr-val">{(pos.rr_ratio||0).toFixed(2)}x</p>
            <p className="xcard-rr-acct">{accountIcon} {pos.account_id}</p>
          </div>
        </div>
        <div className="xcard-sltp">
          <div className="xcard-sltp-labels">
            <span style={{ color:"#ff5d6c" }}>SL ${fmt(pos.stop_loss)}</span>
            <span style={{ color:"var(--text-mute)",fontSize:10 }}>Progress</span>
            <span style={{ color:"#4ade80" }}>TP ${fmt(pos.take_profit)}</span>
          </div>
          <div className="xcard-sltp-track">
            <div className="xcard-sltp-fill" style={{
              width:`${progress}%`,
              background: profit ? "linear-gradient(90deg,rgba(74,222,128,0.4),#4ade80)" : "linear-gradient(90deg,rgba(255,93,108,0.4),#ff5d6c)",
            }} />
            <div className="xcard-sltp-thumb" style={{ left:`${progress}%`, background:profit?"#4ade80":"#ff5d6c" }} />
          </div>
        </div>
        <div className="xcard-data-grid">
          {[
            { label:"Entry",    val:fmt(pos.entry_price),             color:"var(--text)" },
            { label:"Mark",     val:fmt(pos.current_price),            color:profit?"#4ade80":"#ff5d6c" },
            { label:"Liq.",     val:fmt(pos.liquidation_price),        color:"#f5b042" },
            { label:"Size",     val:money(positionSize, { sign: false }),           color:"var(--text)" },
            { label:"Opened",   val:ago(pos.opened_at),                color:"var(--text-dim)" },
            { label:"Conf.",    val:`${confidence}%`, color:"#4ad6ff" },
          ].map(({ label, val, color }) => (
            <div key={label} className="xcard-data-cell">
              <p className="xcard-data-label">{label}</p>
              <p className="xcard-data-val" style={{ color }}>{val}</p>
            </div>
          ))}
        </div>
        <div
          style={{
            margin:"0 14px 12px",
            padding:"12px",
            border:`1px solid ${weex.color}35`,
            borderRadius:14,
            background:"rgba(255,255,255,0.025)",
          }}
        >
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:10 }}>
            <div>
              <div style={{ color:"rgba(255,255,255,0.38)", fontSize:9, textTransform:"uppercase", letterSpacing:"0.22em" }}>
                WEEX execution validation
              </div>
              <div style={{ color:"rgba(255,255,255,0.48)", fontSize:9, marginTop:3 }}>
                Shadow book only · never added to AEON paper P&amp;L
              </div>
            </div>
            <span
              style={{
                color:weex.color,
                background:weex.background,
                border:`1px solid ${weex.color}45`,
                borderRadius:999,
                padding:"4px 7px",
                fontSize:8,
                fontWeight:800,
                letterSpacing:"0.08em",
                whiteSpace:"nowrap",
              }}
            >
              {weex.label}
            </span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:7 }}>
            <InfoCell
              label="WEEX Mark"
              value={validation.weex_mark ? `$${fmt(validation.weex_mark)}` : "Syncing"}
              tone="cyan"
            />
            <InfoCell
              label="Depth Fill"
              value={validation.depth_fill_pct != null ? `${Number(validation.depth_fill_pct).toFixed(1)}%` : "Syncing"}
              tone={Number(validation.depth_fill_pct) >= 99 ? "emerald" : "amber"}
            />
            <InfoCell
              label="Spread / Basis"
              value={
                validation.spread_bps != null || validation.basis_bps != null
                  ? `${Number(validation.spread_bps || 0).toFixed(1)} / ${Number(validation.basis_bps || 0).toFixed(1)} bps`
                  : "Syncing"
              }
              tone="white"
            />
            <InfoCell
              label="Demo Mirror"
              value={
                validation.demo_status === "FILLED"
                  ? `${validation.shadow_scale_pct || 0}% scale`
                  : validation.demo_eligible
                    ? String(validation.demo_status || "PENDING").replace(/_/g, " ")
                    : "New trades only"
              }
              tone={validation.demo_status === "FILLED" ? "emerald" : "amber"}
            />
          </div>
          <p style={{ color:"rgba(255,255,255,0.48)", fontSize:10, lineHeight:1.5, margin:"9px 1px 0" }}>
            {validation.demo_error || weex.reason} Real-money WEEX trading is disabled.
          </p>
        </div>
        <div className="xcard-reason-stack">
          <div className="xcard-reason-head">
            <span>Reason Stack</span>
            <b>{confidence}% confidence</b>
          </div>
          <div className="xcard-reason-rows">
            {reasonRows.map((row) => (
              <div key={row.label} className="xcard-reason-row">
                <div className="xcard-reason-label" style={{ color: row.tone }}>{row.label}</div>
                <p>{row.value}</p>
              </div>
            ))}
          </div>
          <div className="xcard-confirm-strip">
            {confirmations.length > 0 ? confirmations.slice(0, 8).map((c, i) => (
              <span key={`${c}-${i}`} className="xcard-confirm-pill">
                <span className="xcard-sig-dot" style={{ background:"#4ade80" }} />
                {c.slice(0, 44)}
              </span>
            )) : (
              <span className="xcard-confirm-empty">No structured confirmations attached to this position.</span>
            )}
          </div>
        </div>
        <div className="xcard-footer">
          <span className="xcard-engine-chip" style={{ background:ec.bg, border:`1px solid ${ec.border}`, color:ec.text }}>
            <Cpu size={11} /> {strategy}
          </span>
          {actionError && <span style={{ color:"#ff8a94", fontSize:11 }}>{actionError}</span>}
          {onClosePosition && (
            <button type="button" className="xcard-close-pos" onClick={() => onClosePosition(pos.account_id, pos.symbol)}>
              <X size={11} /> Close Position
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Trade calling card row (compact) ──────────────────────────────────────
function TradeCard({ trade, isOpen, onClick }) {
  const dir    = String(trade.direction || "").toUpperCase();
  const isLong = dir === "LONG";
  const symbol = String(trade.symbol || "").replace("/USDT", "");
  const pnlUsd = Number(trade.pnl_usd ?? trade.realized_pnl ?? trade.pnl ?? 0);
  const pnlPct = Number(trade.pnl_pct ?? 0);
  const account= trade.account_id || trade.account || "";
  const engine = labelFor(trade.strategy || trade.engine);
  const lev    = Number(trade.leverage || 0);
  const accent = accentFor(account);
  const theme  = themeFor(String(trade.strategy || trade.engine || "").toLowerCase());
  const isWin  = pnlUsd >= 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`arena-trade-card-compact w-full text-left ${!isOpen && isWin ? "is-win" : !isOpen && !isWin ? "is-loss" : ""}`}
      style={{ "--acc-color": isOpen ? theme.color : accent }}
    >
      <div className="flex items-center gap-2.5">
        {/* Engine glyph or direction icon */}
        {isOpen ? (
          <span
            className="inline-grid place-items-center w-8 h-8 rounded-xl flex-shrink-0 text-sm"
            style={{ background: `${theme.color}12`, color: theme.color, border: `1px solid ${theme.color}25`, textShadow: `0 0 8px ${theme.color}` }}
          >
            {theme.glyph}
          </span>
        ) : (
          <span className="inline-grid place-items-center w-8 h-8 rounded-xl flex-shrink-0"
            style={{ background: isLong ? "rgba(74,222,128,0.1)" : "rgba(255,93,108,0.1)", color: isLong ? "#4ade80" : "#ff5d6c" }}>
            {isLong ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
          </span>
        )}

        {/* Symbol + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-display text-xs font-semibold text-white">{symbol}</span>
            <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full"
              style={{ background: isOpen ? `${theme.color}14` : `color-mix(in srgb, ${accent} 12%, transparent)`, color: isOpen ? theme.color : accent }}>
              {engine}
            </span>
            {lev > 0 && <span className="text-[9px] text-white/30 font-mono">{lev}x</span>}
            {isOpen && (
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "#FFB800", boxShadow: "0 0 5px #FFB800", animation: "arenaPulseDot 1.6s ease-out infinite" }} />
            )}
          </div>
          <div className="text-[9px] text-white/30 font-mono truncate">
            {account} · {fmtAge(trade.entry_time || trade.opened_at || trade.closed_at)}
          </div>
        </div>

        {/* PnL */}
        <div className="text-right flex-shrink-0">
          <div className={`font-mono text-xs font-bold ${isOpen ? "text-amber-300" : isWin ? "text-emerald-300" : "text-rose-300"}`}>
            {pnlUsd >= 0 ? "+" : ""}{fmtUsd(pnlUsd)}
          </div>
          <div className={`text-[9px] font-mono ${isOpen ? "text-amber-400/50" : isWin ? "text-emerald-400/50" : "text-rose-400/50"}`}>
            {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
          </div>
        </div>

        <ChevronRight className="h-3.5 w-3.5 text-white/20 flex-shrink-0" />
      </div>
    </button>
  );
}

// ── Account row ────────────────────────────────────────────────────────────
function AccountRow({ account, active, onFilter, winRate, onViewDetail }) {
  const [expanded, setExpanded] = useState(false);
  const meta  = ACCOUNT_META[account.id] || { color: "#94a3b8", role: account.id, icon: "•" };
  const heat  = Math.round(Number(account.portfolio_heat || 0) * 100);
  const heatC = heat >= 70 ? "#ff5d6c" : heat >= 40 ? "#f5b042" : "#4ade80";
  const bal   = Number(account.balance || 0);
  const pnl   = Number(account.total_pnl || account.unrealized_pnl || 0);
  const wr    = Math.round(winRate || 0);

  return (
    <div className="arena-account-card" style={{ "--acc-color": meta.color, borderColor: active ? meta.color : undefined }}>
      <button type="button" onClick={() => { onFilter(); setExpanded(v => !v); }} className="w-full flex items-center gap-3 text-left">
        <ProgressRing pct={wr} size={40} stroke={3} color={meta.color}>
          <span className="text-[9px] font-mono font-bold" style={{ color: meta.color }}>{wr}</span>
        </ProgressRing>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-display text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: meta.color }}>{account.id}</span>
            {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color, boxShadow: `0 0 6px ${meta.color}` }} />}
          </div>
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/38">{meta.role}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-mono text-sm font-semibold text-white">{fmtUsd(bal, 0)}</div>
          <div className="text-[9px] text-white/38">{account.open_count || 0} open</div>
        </div>
        <ChevronRight className={`h-4 w-4 text-white/25 flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-3 pt-3 space-y-2" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <div>
            <div className="flex justify-between text-[9px] uppercase tracking-[0.18em] mb-1.5">
              <span className="text-white/40">Heat</span>
              <span style={{ color: heatC }}>{heat}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(100, heat)}%`, background: heatC, boxShadow: `0 0 8px ${heatC}` }} />
            </div>
          </div>
          {pnl !== 0 && (
            <div className="flex justify-between items-center">
              <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">PnL</span>
              <span className={`font-mono text-xs font-semibold ${pnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {pnl >= 0 ? "+" : ""}{fmtUsd(pnl)}
              </span>
            </div>
          )}
          {Array.isArray(account.symbols) && account.symbols.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {account.symbols.slice(0, 8).map(s => (
                <span key={s} className="text-[9px] rounded-full px-2 py-0.5 text-white/50" style={{ background: "rgba(255,255,255,0.06)" }}>
                  {s.replace("/USDT", "")}
                </span>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewDetail(account); }}
            className="w-full mt-1 text-[10px] uppercase tracking-[0.2em] py-1.5 rounded-lg transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}
          >
            View all trades →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Today's session card ───────────────────────────────────────────────────
function TodayCard({ closedTrades, equity, liveSessionPnl, openUnrealized = 0 }) {
  const today = new Date().toISOString().slice(0, 10);
  const todayTrades = closedTrades.filter(t => {
    const d = t.closed_at || t.exit_time || t.entry_time || "";
    return String(d).startsWith(today);
  });
  const todayPnl = todayTrades.reduce((s, t) => s + Number(t.pnl_usd ?? t.realized_pnl ?? t.pnl ?? 0), 0);
  const todayWins = todayTrades.filter(t => Number(t.pnl_usd ?? t.pnl ?? 0) > 0).length;
  const todayLosses = todayTrades.length - todayWins;
  // Fallback to equity data when no closed trades today
  const todayEquityPnl = equity
    .filter(e => String(e.date || "").startsWith(today))
    .reduce((s, e) => s + (e.pnl || 0), 0);
  const realizedToday = todayTrades.length > 0 ? todayPnl : todayEquityPnl;
  // Headline = the SAME live session P&L used across the desk (realized today + open
  // unrealized), so every session-P&L display agrees. Falls back to realized if not supplied.
  const displayPnl = Number.isFinite(liveSessionPnl) ? liveSessionPnl : realizedToday;
  const isPos = displayPnl >= 0;
  const accent = isPos ? "#4ade80" : "#ff5d6c";
  const dayLabel = new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  return (
    <section className="relative rounded-2xl p-4 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${accent}07 0%, rgba(0,0,0,0) 60%)`,
        border: `1px solid ${accent}20`,
      }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-[0.32em] text-white/40 mb-2">Today's Session</div>
          <div className={`font-display text-3xl font-semibold ${isPos ? "text-emerald-300" : "text-rose-300"}`}>
            {displayPnl >= 0 ? "+" : "−"}${Math.abs(displayPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-white/40 mt-1.5 font-mono">
            {todayTrades.length} trades · <span className="text-emerald-400/70">{todayWins}W</span> / <span className="text-rose-400/70">{todayLosses}L</span> · open {openUnrealized >= 0 ? "+" : "−"}${Math.abs(openUnrealized).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] text-white/25 font-mono">{dayLabel}</div>
          {todayTrades.length > 0 && (
            <div className="mt-2 flex items-center gap-1 justify-end">
              <div className="h-1.5 rounded-full overflow-hidden flex" style={{ width: 64, background: "rgba(255,255,255,0.07)" }}>
                <div className="h-full bg-emerald-400 transition-all duration-700 rounded-full"
                  style={{ width: `${todayTrades.length > 0 ? (todayWins / todayTrades.length) * 100 : 0}%` }} />
              </div>
              <span className="text-[9px] font-mono text-white/35">
                {todayTrades.length > 0 ? Math.round((todayWins / todayTrades.length) * 100) : 0}%
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Monte Carlo fan chart SVG ──────────────────────────────────────────────
function MCFanChart({ bands, height = 160 }) {
  if (!bands || bands.length < 2) return null;
  const W = 320, H = height;
  const PAD = { t: 8, r: 8, b: 20, l: 44 };
  const cw = W - PAD.l - PAD.r;
  const ch = H - PAD.t - PAD.b;

  const allVals = bands.flatMap(b => [b.p5, b.p95]);
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const span = maxV - minV || 1;

  const tx = (i) => PAD.l + (i / (bands.length - 1)) * cw;
  const ty = (v) => PAD.t + ch - ((v - minV) / span) * ch;

  const pathD = (key) => bands.map((b, i) => `${i === 0 ? "M" : "L"}${tx(i).toFixed(1)},${ty(b[key]).toFixed(1)}`).join(" ");
  const areaD = (k1, k2) => {
    const fwd = bands.map((b, i) => `${i === 0 ? "M" : "L"}${tx(i).toFixed(1)},${ty(b[k1]).toFixed(1)}`).join(" ");
    const rev = [...bands].reverse().map((b, i) => `L${tx(bands.length - 1 - i).toFixed(1)},${ty(b[k2]).toFixed(1)}`).join(" ");
    return `${fwd} ${rev} Z`;
  };

  // zero line y
  const zeroY = ty(0);
  const zeroInRange = zeroY >= PAD.t && zeroY <= PAD.t + ch;

  // y-axis labels
  const yTicks = 3;
  const yLabels = Array.from({ length: yTicks }, (_, i) => {
    const v = minV + (span / (yTicks - 1)) * i;
    const y = ty(v);
    return { v, y };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      {/* zero line */}
      {zeroInRange && (
        <line x1={PAD.l} y1={zeroY} x2={W - PAD.r} y2={zeroY}
          stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4,3" />
      )}
      {/* outer band p5–p95 */}
      <path d={areaD("p5","p95")} fill="rgba(74,214,255,0.05)" />
      {/* inner band p25–p75 */}
      <path d={areaD("p25","p75")} fill="rgba(74,214,255,0.10)" />
      {/* p95 upper edge */}
      <path d={pathD("p95")} fill="none" stroke="rgba(74,214,255,0.25)" strokeWidth="1" />
      {/* p5 lower edge */}
      <path d={pathD("p5")} fill="none" stroke="rgba(255,93,108,0.25)" strokeWidth="1" />
      {/* median p50 */}
      <path d={pathD("p50")} fill="none" stroke="#4ad6ff" strokeWidth="1.8" strokeLinecap="round" />
      {/* y-axis labels */}
      {yLabels.map(({ v, y }) => (
        <text key={v} x={PAD.l - 4} y={y + 3} textAnchor="end" fontSize="7"
          fill="rgba(255,255,255,0.3)" fontFamily="monospace">
          {v >= 0 ? `+$${Math.abs(v) >= 1000 ? (v/1000).toFixed(1)+"k" : v.toFixed(0)}`
                  : `-$${Math.abs(v) >= 1000 ? (Math.abs(v)/1000).toFixed(1)+"k" : Math.abs(v).toFixed(0)}`}
        </text>
      ))}
      {/* x-axis labels */}
      {[0, Math.floor(bands.length / 2), bands.length - 1].map(i => (
        <text key={i} x={tx(i)} y={H - 4} textAnchor="middle" fontSize="7"
          fill="rgba(255,255,255,0.25)" fontFamily="monospace">
          T{bands[i]?.trade}
        </text>
      ))}
    </svg>
  );
}

// ── Monte Carlo simulation panel ───────────────────────────────────────────
function MonteCarloPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [ran, setRan]         = useState(false);

  const run = useCallback(() => {
    setLoading(true);
    marketGet(`${API}/backtest/v21/monte-carlo?simulations=500&sample_size=150`, axCfg)
      .then(r => { setData(r.data); setRan(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { run(); }, [run]);

  const fp  = data?.final_pnl_dist;
  const dd  = data?.max_drawdown_dist;
  const profitable = fp?.profitable_pct ?? 0;
  const medianPnl  = fp?.p50 ?? 0;
  const bestCase   = fp?.p95 ?? 0;
  const medianDD   = dd?.p50 ?? 0;

  const statsRows = [
    { label: "Profitable sims",  val: `${profitable.toFixed(1)}%`,     color: profitable >= 50 ? "#4ade80" : profitable >= 25 ? "#f5b042" : "#ff5d6c" },
    { label: "Median outcome",   val: fmtUsd(medianPnl),                color: medianPnl >= 0 ? "#4ade80" : "#ff5d6c" },
    { label: "Best case (p95)",  val: fmtUsd(bestCase),                 color: bestCase >= 0 ? "#4ade80" : "#f5b042" },
    { label: "Median drawdown",  val: `-${fmtUsd(Math.abs(medianDD))}`, color: "#f5b042" },
  ];

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-3.5 w-3.5 text-cyan-400" />
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Monte Carlo</div>
          {ran && data && (
            <span className="text-[9px] text-white/25 font-mono">
              {data.simulations} sims · {data.source_trades} trades
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.18em] transition-all"
          style={{
            background: loading ? "rgba(255,255,255,0.04)" : "rgba(74,214,255,0.1)",
            border: "1px solid rgba(74,214,255,0.25)",
            color: loading ? "rgba(255,255,255,0.3)" : "#4ad6ff",
          }}
        >
          {loading ? (
            <><span style={{ animation: "arenaSpin 1s linear infinite", display:"inline-block" }}>↻</span> Running…</>
          ) : "↻ Re-run"}
        </button>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(74,214,255,0.12)", background: "rgba(6,8,20,0.6)" }}>
        {/* Legend */}
        <div className="flex items-center gap-4 px-3 pt-3 pb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-[2px] rounded-full" style={{ background: "#4ad6ff" }} />
            <span className="text-[8px] text-white/40 uppercase tracking-[0.16em]">Median</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-2.5 rounded-sm" style={{ background: "rgba(74,214,255,0.10)" }} />
            <span className="text-[8px] text-white/40 uppercase tracking-[0.16em]">25–75%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-2.5 rounded-sm" style={{ background: "rgba(74,214,255,0.05)" }} />
            <span className="text-[8px] text-white/40 uppercase tracking-[0.16em]">5–95%</span>
          </div>
          {!ran && !loading && (
            <span className="ml-auto text-[8px] text-white/25 uppercase tracking-[0.14em]">Loading…</span>
          )}
        </div>

        {/* Fan chart */}
        <div className="px-1">
          {data?.curve_bands?.length > 0
            ? <MCFanChart bands={data.curve_bands} height={160} />
            : (
              <div className="h-40 flex items-center justify-center">
                <span className="text-[10px] text-white/25 uppercase tracking-[0.22em]">
                  {loading ? "Simulating…" : "No data"}
                </span>
              </div>
            )
          }
        </div>

        {/* Stats row */}
        {data && (
          <div className="grid grid-cols-2 gap-px border-t border-white/[0.06] mt-1">
            {statsRows.map(({ label, val, color }) => (
              <div key={label} className="px-3 py-2.5" style={{ borderBottom: "none" }}>
                <div className="text-[8px] uppercase tracking-[0.2em] text-white/35 mb-0.5">{label}</div>
                <div className="font-mono text-xs font-semibold" style={{ color }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Drawdown percentile bar */}
        {dd && (
          <div className="px-3 pb-3 pt-1 border-t border-white/[0.05]">
            <div className="flex justify-between text-[8px] text-white/30 uppercase tracking-[0.16em] mb-1.5">
              <span>Max Drawdown Distribution</span>
              <span className="font-mono">{fmtUsd(dd.p50)} median</span>
            </div>
            <div className="relative h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
              {/* p25-p75 zone */}
              <div className="absolute top-0 h-full rounded-full"
                style={{
                  left: `${(dd.p25 / (dd.p95 || 1)) * 100}%`,
                  width: `${((dd.p75 - dd.p25) / (dd.p95 || 1)) * 100}%`,
                  background: "rgba(245,176,66,0.5)",
                }} />
              {/* median tick */}
              <div className="absolute top-0 h-full w-0.5 rounded-full"
                style={{ left: `${(dd.p50 / (dd.p95 || 1)) * 100}%`, background: "#f5b042" }} />
            </div>
            <div className="flex justify-between text-[8px] text-white/20 font-mono mt-1">
              <span>{fmtUsd(dd.p5)}</span>
              <span>{fmtUsd(dd.p95)}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Signal Scorecard — were the broadcast Telegram setups actually good? ───
function SignalScorecard() {
  const [d, setD]           = useState(null);
  const [loading, setLoad]  = useState(false);

  const load = useCallback(() => {
    setLoad(true);
    marketGet(`${API}/analytics/signal-scorecard`, axCfg)
      .then(r => setD(r.data?.error ? null : r.data))
      .catch(() => setD(null))
      .finally(() => setLoad(false));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 120000); return () => clearInterval(id); }, [load]);

  if (!d) {
    return (
      <section>
        <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">Signal Scorecard</div>
        <div className="rounded-2xl h-28 flex items-center justify-center" style={{ border: "1px solid rgba(74,214,255,0.12)", background: "rgba(6,8,20,0.6)" }}>
          <span className="text-[10px] text-white/25 uppercase tracking-[0.22em]">{loading ? "Scoring signals…" : "No scorecard yet"}</span>
        </div>
      </section>
    );
  }

  const h = d.headline || {};
  const ded = d.deduped || {};
  const curvePts = (ded.all?.curve || []).map(p => ({ y: p.cum_usd }));
  const signed = (v) => `${v >= 0 ? "+" : "−"}$${fmtUsd(Math.abs(v))}`;

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Flame className="h-3.5 w-3.5 text-amber-400" />
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Signal Scorecard</div>
          <span className="text-[9px] text-white/25 font-mono">{d.setup_count} setups · {d.window_days}d · {ded.all?.win_rate}% win</span>
        </div>
        <span className="text-[8px] uppercase tracking-[0.16em] text-white/25">if every signal taken</span>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(245,176,66,0.18)", background: "rgba(6,8,20,0.6)" }}>
        {/* The gap — the actionable headline */}
        <div className="grid grid-cols-3 gap-px border-b border-white/[0.06]">
          {[
            ["Edge available", h.edge_available_usd, "#4ade80", "signals, if followed"],
            ["Filter kept", h.edge_captured_usd, "#f5b042", "passed the gate"],
            ["Left on table", -Math.abs(h.edge_left_on_table_usd), "#ff5d6c", "blocked by filters"],
          ].map(([lbl, val, color, sub]) => (
            <div key={lbl} className="px-3 py-3">
              <div className="text-[8px] uppercase tracking-[0.18em] text-white/35 mb-1">{lbl}</div>
              <div className="font-display text-lg font-semibold" style={{ color }}>{signed(val || 0)}</div>
              <div className="text-[8px] text-white/30 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>

        {/* Reality check */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06]"
          style={{ background: "rgba(255,93,108,0.05)" }}>
          <span className="text-[9px] uppercase tracking-[0.18em] text-white/45">AEON actually realized (same window)</span>
          <span className={`font-mono text-sm font-semibold ${(d.actual_realized?.pnl ?? 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {signed(d.actual_realized?.pnl ?? 0)} · {d.actual_realized?.win_rate}% win
          </span>
        </div>

        {/* If-followed equity curve */}
        <div className="px-1 pt-2">
          <EquityCurveCanvas points={curvePts} height={120} color="#4ade80" glow="#f5b042" showAxis ariaLabel="If-followed equity (R→$)" />
        </div>

        {/* Direction split */}
        <div className="grid grid-cols-2 gap-px border-t border-white/[0.06]">
          {[
            ["SHORT", ded.short_only, "#4ade80"],
            ["LONG", ded.long_only, "#ff5d6c"],
          ].map(([lbl, s, c]) => (
            <div key={lbl} className="px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">{lbl}</span>
                <span className="font-mono text-[10px] text-white/50">{s?.win_rate}% win</span>
              </div>
              <div className="font-mono text-sm font-semibold mt-0.5" style={{ color: (s?.usd ?? 0) >= 0 ? "#4ade80" : "#ff5d6c" }}>
                {signed(s?.usd ?? 0)}
              </div>
            </div>
          ))}
        </div>

        {/* Weekly bias + edge */}
        <div className="border-t border-white/[0.06]">
          <div className="grid grid-cols-[1.2fr_0.8fr_0.7fr_0.9fr] gap-1 px-3 py-1.5">
            {["Week", "Bias", "Win%", "R"].map((x, i) => (
              <div key={x} className={`text-[8px] uppercase tracking-[0.16em] text-white/30 ${i === 0 ? "" : "text-right"}`}>{x}</div>
            ))}
          </div>
          {(d.by_week || []).map((w) => (
            <div key={w.week} className="grid grid-cols-[1.2fr_0.8fr_0.7fr_0.9fr] gap-1 px-3 py-1.5 border-t border-white/[0.03] items-center">
              <span className="font-mono text-[10px] text-white/70">{w.week?.slice(5)}</span>
              <span className="text-right text-[9px] font-mono uppercase" style={{ color: w.bias === "LONG" ? "#4ade80" : w.bias === "SHORT" ? "#ff8a5d" : "#9aa" }}>{w.bias}</span>
              <span className="text-right font-mono text-[10px] text-white/55">{w.win_rate}%</span>
              <span className={`text-right font-mono text-[10px] ${w.r_sum >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{w.r_sum >= 0 ? "+" : ""}{w.r_sum}R</span>
            </div>
          ))}
        </div>

        <div className="px-3 py-2 border-t border-white/[0.05] text-[8px] text-white/25 leading-relaxed">
          Deduped 1-position/symbol backtest vs real price · ${d.risk_per_trade}/trade risk ·
          no fees/slippage · same-bar = loss (conservative). Signals were directionally sound — the loss is the filter
          blocking the best setups + execution giving back the rest.
        </div>
      </div>
    </section>
  );
}

// ── Session Ledger — day-by-day accumulated P&L ────────────────────────────
function SessionLedger() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  // Time window: false = all-time, true = last 30 days (current-config performance)
  const [window30, setWindow30] = useState(false);
  // Live unrealized P&L of the open positions — floats with current asset prices
  const [open, setOpen]         = useState(null);

  const load = useCallback((recent) => {
    setLoading(true);
    // Scoped to current active accounts, matching how the rest of the system
    // (get_stats, etc.) treats retired accounts — old configs like PRO/STARTER
    // accumulated large early losses before being shut down, and mixing that
    // into the "current" total would misrepresent how the live system is doing.
    marketGet(`${API}/analytics/daily-pnl${recent ? "?days=30" : ""}`, axCfg)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(window30); }, [load, window30]);

  // Poll the live open positions so the unrealized number ticks with price
  useEffect(() => {
    let alive = true;
    const fetchOpen = () => {
      marketGet(`${API}/positions`, axCfg)
        .then(r => { if (alive) setOpen(r.data); })
        .catch(() => {});
    };
    fetchOpen();
    const id = setInterval(fetchOpen, 12000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const openPnl   = open?.total_unrealized_usd ?? null;
  const openCount = open?.total ?? 0;

  // newest session first; cumulative is the accumulated total AS OF that date
  const rows = useMemo(() => (data?.days || []).slice().reverse(), [data]);
  const totalPnl = data?.total_pnl ?? 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5 text-cyan-400" />
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Session Ledger</div>
          {data && (
            <span className="text-[9px] text-white/25 font-mono">
              {data.session_count} sessions · {data.total_trades} trades
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setWindow30(v => !v)}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.18em] transition-all"
          style={{
            background: window30 ? "rgba(74,214,255,0.1)" : "rgba(245,176,66,0.12)",
            border: `1px solid ${window30 ? "rgba(74,214,255,0.25)" : "rgba(245,176,66,0.35)"}`,
            color: window30 ? "#4ad6ff" : "#f5b042",
          }}
        >
          {window30 ? "Last 30 days" : "Active accounts"}
        </button>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(74,214,255,0.12)", background: "rgba(6,8,20,0.6)" }}>
        {/* Accumulated total banner */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06]">
          <span className="text-[8px] uppercase tracking-[0.2em] text-white/35">
            {window30 ? "Accumulated P&L (last 30 days)" : "Accumulated P&L (active accounts)"}
          </span>
          <span className={`font-mono text-sm font-semibold ${totalPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {totalPnl >= 0 ? "+" : "−"}{fmtUsd(Math.abs(totalPnl))}
          </span>
        </div>

        {/* Live unrealized P&L of open positions — floats with current prices */}
        {openPnl !== null && (
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]"
            style={{ background: "rgba(74,214,255,0.04)" }}>
            <span className="flex items-center gap-1.5 text-[8px] uppercase tracking-[0.2em] text-cyan-300/60">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Open / live · {openCount} {openCount === 1 ? "trade" : "trades"}
            </span>
            <span className={`font-mono text-sm font-semibold ${openPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
              {openPnl >= 0 ? "+" : "−"}{fmtUsd(Math.abs(openPnl))}
            </span>
          </div>
        )}

        {/* Column header */}
        <div className="grid grid-cols-[1.2fr_0.7fr_1fr_1.1fr] gap-1 px-3 py-1.5 border-b border-white/[0.05]">
          {["Date", "Trades", "Day P&L", "Accumulated"].map((h, i) => (
            <div key={h} className={`text-[8px] uppercase tracking-[0.16em] text-white/30 ${i === 0 ? "" : "text-right"}`}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        <div className="max-h-[360px] overflow-y-auto">
          {loading && !rows.length && (
            <div className="h-24 flex items-center justify-center">
              <span className="text-[10px] text-white/25 uppercase tracking-[0.22em]">Loading sessions…</span>
            </div>
          )}
          {!loading && !rows.length && (
            <div className="h-24 flex items-center justify-center">
              <span className="text-[10px] text-white/25 uppercase tracking-[0.22em]">No sessions</span>
            </div>
          )}
          {rows.map((r) => (
            <div key={r.date}
              className="grid grid-cols-[1.2fr_0.7fr_1fr_1.1fr] gap-1 px-3 py-2 border-b border-white/[0.03] items-center">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[11px] text-white/80">{r.date}</span>
                {r.retired_trades > 0 && (
                  <span className="text-[7px] uppercase tracking-[0.1em] px-1 py-0.5 rounded"
                    style={{ background: "rgba(245,176,66,0.12)", color: "#f5b042" }}>ret</span>
                )}
              </div>
              <div className="text-right font-mono text-[11px] text-white/55">{r.trades}</div>
              <div className={`text-right font-mono text-[11px] font-semibold ${r.day_pnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {r.day_pnl >= 0 ? "+" : "−"}{fmtUsd(Math.abs(r.day_pnl))}
              </div>
              <div className={`text-right font-mono text-[11px] ${r.cumulative_pnl >= 0 ? "text-emerald-200/80" : "text-rose-200/80"}`}>
                {r.cumulative_pnl >= 0 ? "+" : "−"}{fmtUsd(Math.abs(r.cumulative_pnl))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Best / worst trade highlight cards ────────────────────────────────────
function BestWorstTrades({ closedTrades, onSelect }) {
  const { best, worst } = useMemo(() => {
    if (!closedTrades.length) return { best: null, worst: null };
    let best = closedTrades[0], worst = closedTrades[0];
    for (const t of closedTrades) {
      const p = Number(t.pnl_usd ?? t.realized_pnl ?? t.pnl ?? 0);
      if (p > Number(best.pnl_usd ?? best.realized_pnl ?? best.pnl ?? 0)) best = t;
      if (p < Number(worst.pnl_usd ?? worst.realized_pnl ?? worst.pnl ?? 0)) worst = t;
    }
    return { best, worst };
  }, [closedTrades]);

  if (!best) return null;

  const Highlight = ({ trade, label, accentColor, badge }) => {
    const symbol = String(trade.symbol || "").replace("/USDT", "");
    const pnl = Number(trade.pnl_usd ?? trade.realized_pnl ?? trade.pnl ?? 0);
    const theme = themeFor(String(trade.strategy || trade.engine || "").toLowerCase());
    return (
      <button type="button" onClick={() => onSelect(trade)}
        className="flex-1 rounded-2xl p-3 text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
        style={{ background: `linear-gradient(135deg, ${accentColor}09 0%, rgba(0,0,0,0) 100%)`, border: `1px solid ${accentColor}25` }}>
        <div className="text-[8px] uppercase tracking-[0.28em] mb-1.5 flex items-center gap-1" style={{ color: accentColor }}>
          <span>{badge}</span>{label}
        </div>
        <div className="font-display text-base font-semibold leading-tight" style={{ color: accentColor }}>
          {pnl >= 0 ? "+" : ""}{fmtUsd(pnl)}
        </div>
        <div className="text-[9px] text-white/40 mt-0.5 font-mono truncate">{symbol} · {theme.label}</div>
      </button>
    );
  };

  return (
    <div className="flex gap-2">
      <Highlight trade={best}  label="Best Trade"  accentColor="#4ade80" badge="★" />
      <Highlight trade={worst} label="Worst Trade" accentColor="#ff5d6c" badge="↓" />
    </div>
  );
}

// ── Close Reason Breakdown ─────────────────────────────────────────────────
function CloseReasonBreakdown({ closedTrades }) {
  const ITEMS_DEF = [
    { key: "take_profit",   label: "Take Profit",  color: "#4ade80" },
    { key: "trailing_stop", label: "Trailing Stop", color: "#4ad6ff" },
    { key: "stop_loss",     label: "Stop Loss",    color: "#ff5d6c" },
    { key: "liquidation",   label: "Liquidation",  color: "#ff2d55" },
    { key: "other",         label: "Other",        color: "#94a3b8" },
  ];

  const items = useMemo(() => {
    const map = { take_profit: 0, trailing_stop: 0, stop_loss: 0, liquidation: 0, other: 0 };
    for (const t of closedTrades) {
      const r = String(t.close_reason || t.exit_reason || "").toLowerCase().replace(/ /g, "_");
      if (r in map) map[r]++;
      else if (r) map.other++;
    }
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return { total, rows: ITEMS_DEF.map(d => ({ ...d, count: map[d.key], pct: total > 0 ? map[d.key] / total : 0 })).filter(d => d.count > 0) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedTrades]);

  if (items.total === 0) return null;

  const R = 44, r = 28, cx = 56, cy = 56;
  let cum = -Math.PI / 2;
  const slices = items.rows.map(item => {
    const angle = item.pct * Math.PI * 2;
    const sa = cum; cum += angle; const ea = cum;
    const large = angle > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(sa), y1 = cy + R * Math.sin(sa);
    const x2 = cx + R * Math.cos(ea), y2 = cy + R * Math.sin(ea);
    const xi1 = cx + r * Math.cos(sa), yi1 = cy + r * Math.sin(sa);
    const xi2 = cx + r * Math.cos(ea), yi2 = cy + r * Math.sin(ea);
    const d = `M${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 ${large},1 ${x2.toFixed(1)},${y2.toFixed(1)} L${xi2.toFixed(1)},${yi2.toFixed(1)} A${r},${r} 0 ${large},0 ${xi1.toFixed(1)},${yi1.toFixed(1)} Z`;
    return { ...item, d };
  });

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">Close Reason</span>
        <span className="text-[9px] text-white/25 font-mono">{items.total} trades</span>
      </div>
      <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.09)", background: "rgba(6,8,20,0.6)" }}>
        <div className="flex items-center gap-5 p-4">
          <svg viewBox="0 0 112 112" width={84} height={84} className="flex-shrink-0">
            {slices.map((s, i) => <path key={i} d={s.d} fill={s.color} opacity={0.88} />)}
            <circle cx={cx} cy={cy} r={r - 3} fill="rgba(6,8,20,0.92)" />
            <text x={cx} y={cy - 5} textAnchor="middle" fontSize="13" fill="white" fontWeight="bold" fontFamily="ui-monospace,monospace">{items.total}</text>
            <text x={cx} y={cy + 8} textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.35)" fontFamily="ui-sans-serif">exits</text>
          </svg>
          <div className="flex-1 space-y-2 min-w-0">
            {slices.map(item => (
              <div key={item.key} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <span className="text-[10px] text-white/60 truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className="h-1 rounded-full" style={{ width: Math.max(16, item.pct * 72), background: item.color, opacity: 0.65 }} />
                  <span className="text-[10px] font-mono text-white/45 w-8 text-right">{(item.pct * 100).toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Trade Return Distribution ───────────────────────────────────────────────
function TradeReturnDistribution({ closedTrades }) {
  const BUCKETS = [
    { label: "<-20%", min: -Infinity, max: -20, color: "#ff2d55" },
    { label: "-20%",  min: -20,       max: -10, color: "#ff5d6c" },
    { label: "-10%",  min: -10,       max: 0,   color: "#ff8a94" },
    { label: "0%",    min: 0,         max: 10,  color: "#4ade80" },
    { label: "+10%",  min: 10,        max: 20,  color: "#22c55e" },
    { label: ">+20%", min: 20,        max: Infinity, color: "#16a34a" },
  ];

  const counts = useMemo(() =>
    BUCKETS.map(b => ({
      ...b,
      count: closedTrades.filter(t => {
        const p = Number(t.pnl_pct ?? 0);
        return p >= b.min && p < b.max;
      }).length,
    })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [closedTrades]);

  const total = counts.reduce((s, b) => s + b.count, 0);
  if (total === 0) return null;
  const maxCount = Math.max(...counts.map(b => b.count), 1);

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">Return Distribution</span>
        <span className="text-[9px] text-white/25 font-mono">{total} trades</span>
      </div>
      <div className="rounded-2xl overflow-hidden px-4 pt-4 pb-3" style={{ border: "1px solid rgba(255,255,255,0.09)", background: "rgba(6,8,20,0.6)" }}>
        <div className="flex items-end gap-2" style={{ height: 84 }}>
          {counts.map((b, i) => {
            const h = Math.max(b.count > 0 ? 3 : 0, Math.round((b.count / maxCount) * 68));
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                {b.count > 0 && <span className="text-[8px] font-mono text-white/40">{b.count}</span>}
                <div className="w-full rounded-t-sm"
                  style={{ height: h, background: b.color, opacity: b.count > 0 ? 0.85 : 0.1, boxShadow: b.count > 0 ? `0 0 8px ${b.color}55` : "none", transition: "height 0.6s ease" }} />
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 mt-1.5">
          {counts.map((b, i) => (
            <div key={i} className="flex-1 text-center">
              <span className="text-[7px] font-mono text-white/28 leading-none">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Confidence vs Win Rate ─────────────────────────────────────────────────
function ConfidenceWinRate({ closedTrades }) {
  const BUCKETS = [
    { label: "< 70%", min: 0,  max: 70 },
    { label: "70–80%", min: 70, max: 80 },
    { label: "80–90%", min: 80, max: 90 },
    { label: "90%+",   min: 90, max: 101 },
  ];

  const data = useMemo(() => {
    const rows = BUCKETS.map(b => {
      const bucket = closedTrades.filter(t => {
        const conf = Number(t.signal_data?.confidence || t.confidence || 0);
        return conf >= b.min && conf < b.max;
      });
      const wins = bucket.filter(t => Number(t.pnl_pct ?? 0) > 0).length;
      const graded = bucket.filter(t => Number(t.pnl_pct ?? 0) !== 0).length;
      return { ...b, count: bucket.length, wr: graded > 0 ? (wins / graded) * 100 : null };
    });
    return rows.filter(r => r.count > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedTrades]);

  if (data.length === 0) return null;
  const wrColor = (wr) => wr >= 60 ? "#4ade80" : wr >= 40 ? "#f5b042" : "#ff5d6c";

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">Confidence vs Win Rate</span>
      </div>
      <div className="rounded-2xl overflow-hidden p-4" style={{ border: "1px solid rgba(255,255,255,0.09)", background: "rgba(6,8,20,0.6)" }}>
        <div className="space-y-3">
          {data.map((b, i) => {
            const wr = b.wr ?? 0;
            const color = b.wr !== null ? wrColor(wr) : "#94a3b8";
            return (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] font-mono text-white/50">{b.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[8px] text-white/28 font-mono">{b.count} trades</span>
                    <span className="text-[10px] font-mono font-semibold" style={{ color }}>
                      {b.wr !== null ? `${wr.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, wr)}%`, background: color, boxShadow: `0 0 7px ${color}80`, transition: "width 0.7s ease" }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-2 border-t border-white/[0.05]">
          <span className="text-[8px] text-white/22 uppercase tracking-[0.14em]">Higher confidence → validates gate signal quality</span>
        </div>
      </div>
    </section>
  );
}

const TIMEFRAMES = ["5m", "15m", "30m", "1D", "1W"];

// ── Root component ─────────────────────────────────────────────────────────
export default function TradingArenaPage({ portfolioSummary, openPositions, refreshTick, marketChart }) {
  const [equity, setEquity] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [positionError, setPositionError] = useState("");
  const [deskErrors, setDeskErrors] = useState([]);
  const [systemPulse, setSystemPulse] = useState(null);
  const [weexShadow, setWeexShadow] = useState(null);
  const [todayRealized, setTodayRealized] = useState(0); // today's closed/realized P&L (UTC)
  const [lastUpdated, setLastUpdated] = useState(null);
  const [workspaceView, setWorkspaceView] = useState("live");
  const [selectedPos, setSelectedPos] = useState(null);
  const [positionActionError, setPositionActionError] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [tradeView, setTradeView] = useState("open");
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [quickFilteredTrades, setQuickFilteredTrades] = useState(null);

  // Account detail modal
  const [detailAccount, setDetailAccount] = useState(null);
  const [detailData, setDetailData]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab]         = useState("overview");

  const openDetailModal = useCallback(async (account) => {
    setDetailAccount(account);
    setDetailData(null);
    setDetailTab("overview");
    setDetailLoading(true);
    try {
      const res = await marketGet(`${API}/paper/account-trades/${account.id}`, axCfg);
      setDetailData(res.data);
    } catch { setDetailData(null); }
    finally  { setDetailLoading(false); }
  }, []);
  const [chartMode, setChartMode] = useState("equity"); // "equity" | "pnl"
  const [chartTf, setChartTf] = useState("1D");
  const [sortBy, setSortBy] = useState("time"); // "time" | "pnl" | "engine"
  const [engineFilter, setEngineFilter] = useState("ALL");
  // How many rows of the (already filtered/sorted) closed-trade list to render —
  // the full list can now be thousands of trades, so this is revealed
  // progressively via "Load more" rather than fetched/rendered all at once.
  const [ledgerVisibleCount, setLedgerVisibleCount] = useState(60);
  useEffect(() => { setLedgerVisibleCount(60); }, [accountFilter, engineFilter, sortBy, tradeView]);

  const load = useCallback(async () => {
    try {
      const [eq, cl, dp, pulse] = await Promise.allSettled([
        // Active accounts only (the 4 in paper_accounts) — every total on the tab shares this scope.
        marketGet(`${API}/analytics/equity-curve?days=30&scope=active`, axCfg),
        // Full history, not a recent slice — covers active + retired accounts
        // (backend caps at 5000; current total closed trades is ~2,900).
        marketGet(`${API}/trading/v2/closed?limit=5000&include_archived=true`, axCfg),
        // Days bucketed in the operator's zone, so "today" matches the header clock.
        marketGet(`${API}/analytics/daily-pnl?scope=active&tz=operator&days=3`, axCfg),
        marketGet(`${API}/system/pulse`, axCfg),
      ]);
      if (eq.status === "fulfilled" && Array.isArray(eq.value.data)) setEquity(eq.value.data);
      if (cl.status === "fulfilled") setClosedTrades(cl.value.data?.closed_trades || cl.value.data || []);
      if (dp.status === "fulfilled") {
        // Today's realized P&L — the operator-zone calendar day.
        const days = dp.value.data?.days || [];
        const zone = dp.value.data?.timezone || (pulse.status === "fulfilled" && pulse.value.data?.operator_tz) || undefined;
        const todayStr = dayKey(new Date(), zone);
        const todayRow = days.find(d => d.date === todayStr);
        setTodayRealized(Number(todayRow?.day_pnl || 0));
      }
      if (pulse.status === "fulfilled") setSystemPulse(pulse.value.data);
      const failed = [
        ["equity", eq],
        ["closed trades", cl],
        ["daily P&L", dp],
        ["system pulse", pulse],
      ].filter(([, result]) => result.status !== "fulfilled").map(([name]) => name);
      setDeskErrors(failed);
      if (failed.length < 4) setLastUpdated(new Date());
    } catch {
      setDeskErrors(["desk data"]);
    }
  }, []);

  useEffect(() => {
    let intervalId;
    const startId = setTimeout(() => {
      load();
      intervalId = setInterval(load, 10000);
    }, 900);
    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [load, refreshTick]);

  const fetchPositions = useCallback(() => {
    // One canonical snapshot drives cards, chart overlays, P&L, and the open
    // ledger so every view uses the same OKX mark and calculation.
    marketGet(`${API}/positions`, axCfg)
      .then((response) => {
        const list = Array.isArray(response.data?.positions) ? response.data.positions : [];
        setPositions(list);
        setOpenTrades(list);
        setWeexShadow(response.data?.weex_shadow || null);
        setPositionError("");
        setLastUpdated(new Date());
      })
      .catch((error) => {
        setPositionError(error?.message || "Position feed unavailable");
      })
      .finally(() => setPositionsLoading(false));
  }, []);

  useEffect(() => {
    let intervalId;
    const startId = setTimeout(() => {
      fetchPositions();
      intervalId = setInterval(fetchPositions, 6000);
    }, 1250);
    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetchPositions, refreshTick]);

  useEffect(() => {
    setSelectedPos((current) => {
      if (!current) return null;
      return positions.find((position) =>
        String(position.id) === String(current.id) &&
        String(position.account_id) === String(current.account_id)
      ) || current;
    });
  }, [positions]);

  const refreshDesk = useCallback(() => {
    load();
    fetchPositions();
  }, [fetchPositions, load]);

  const closePosition = useCallback((accountId, symbol) => {
    setPositionActionError("");
    axios.post(`${API}/paper/close`, { account_id: accountId, symbol }, axCfg)
      .then((response) => {
        if (response.data?.error) throw new Error(response.data.error);
        setSelectedPos(null);
        fetchPositions();
      })
      .catch((error) => {
        setPositionActionError(error?.response?.data?.detail || error?.message || "Position close failed");
      });
  }, [fetchPositions]);

  const { equityPoints, pnlBuckets } = useMemo(() => ({
    equityPoints: buildEquityWindow(equity, chartTf),
    pnlBuckets:   buildBuckets(equity, chartTf),
  }), [equity, chartTf]);

  const totalPnl  = equity.length ? Number(equity[equity.length - 1]?.cumulative_pnl || 0) : 0;
  const windowPnl = equityPoints.length ? equityPoints[equityPoints.length - 1]?.y || 0 : 0;

  // ── Live session P&L ──────────────────────────────────────────────────────
  // Sum of every open position's unrealized P&L (floats with current prices),
  // polled every 6s. Combined with today's realized = the live session total.
  const openUnrealized = useMemo(
    () => positions.reduce((s, p) => s + (Number(p.unrealized_pnl) || 0), 0),
    [positions],
  );
  const sessionLivePnl = todayRealized + openUnrealized;
  const peak      = equity.length ? Math.max(...equity.map(e => Number(e.cumulative_pnl || 0))) : 0;
  const drawdown  = totalPnl - peak;

  const visibleTrades = useMemo(() =>
    closedTrades.filter(t => !HIDDEN_ACCOUNTS.has(t.account_id || t.account || "")),
    [closedTrades],
  );

  const directionStats = useMemo(() => {
    const shorts = visibleTrades.filter(t => String(t.direction || "").toUpperCase() === "SHORT");
    const longs  = visibleTrades.filter(t => String(t.direction || "").toUpperCase() === "LONG");
    const shortWins  = shorts.filter(t => Number(t.pnl_pct ?? 0) > 0).length;
    const longWins   = longs.filter(t => Number(t.pnl_pct ?? 0) > 0).length;
    const shortPnl   = shorts.reduce((s, t) => s + (Number(t.realized_pnl ?? t.pnl_usd ?? 0)), 0);
    const longPnl    = longs.reduce((s, t) => s + (Number(t.realized_pnl ?? t.pnl_usd ?? 0)), 0);
    return {
      shortCount: shorts.length, shortWins,
      shortWr: shorts.length ? Math.round(shortWins / shorts.length * 100) : 0,
      shortPnl: Math.round(shortPnl),
      longCount: longs.length, longWins,
      longWr: longs.length ? Math.round(longWins / longs.length * 100) : 0,
      longPnl: Math.round(longPnl),
    };
  }, [visibleTrades]);

  const winRate = useMemo(() => {
    if (!visibleTrades.length) return 0;
    const wins = visibleTrades.filter(t => Number(t.pnl_pct ?? 0) > 0).length;
    const losses = visibleTrades.filter(t => Number(t.pnl_pct ?? 0) < 0).length;
    return (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0;
  }, [visibleTrades]);

  const accounts = useMemo(() => {
    const summaries = portfolioSummary?.accounts || {};
    const accountIds = new Set([
      ...Object.keys(summaries),
      ...positions.map((position) => position.account_id).filter(Boolean),
    ]);
    return [...accountIds]
      .filter((id) => !HIDDEN_ACCOUNTS.has(id))
      .map((id) => {
        const info = summaries[id] || {};
        const live = positions.filter((position) => position.account_id === id);
        return {
          id,
          ...info,
          open_count: live.length,
          unrealized_pnl: live.reduce((sum, position) => sum + Number(position.unrealized_pnl || 0), 0),
          total_margin_in_use: live.reduce((sum, position) => sum + Number(position.margin || 0), 0),
          symbols: [...new Set(live.map((position) => position.symbol).filter(Boolean))],
        };
      });
  }, [portfolioSummary, positions]);

  const accountWinRates = useMemo(() => {
    const map = {};
    for (const t of visibleTrades) {
      const acc = t.account_id || t.account || "UNKNOWN";
      if (!map[acc]) map[acc] = { wins: 0, losses: 0 };
      const pnl = Number(t.pnl_pct ?? 0);
      if (pnl > 0) map[acc].wins++;
      else if (pnl < 0) map[acc].losses++;
    }
    return Object.fromEntries(
      Object.entries(map).map(([k, v]) => {
        const total = v.wins + v.losses;
        return [k, total > 0 ? (v.wins / total) * 100 : 0];
      })
    );
  }, [visibleTrades]);

  const engineOptions = useMemo(() => {
    const seen = new Set();
    for (const t of visibleTrades) {
      const eng = String(t.strategy || t.engine || "").toLowerCase();
      if (eng) seen.add(eng);
    }
    return Array.from(seen).sort();
  }, [visibleTrades]);

  const sourceTrades = tradeView === "open" ? openTrades : visibleTrades;
  const sortedFiltered = useMemo(() => {
    let list = sourceTrades.filter(t => {
      const accOk = accountFilter === "ALL" || String(t.account_id || t.account || "") === accountFilter;
      const engOk = engineFilter === "ALL" || String(t.strategy || t.engine || "").toLowerCase() === engineFilter;
      return accOk && engOk;
    });
    if (sortBy === "pnl") {
      list = [...list].sort((a, b) => Number(b.pnl_pct ?? 0) - Number(a.pnl_pct ?? 0));
    } else if (sortBy === "engine") {
      list = [...list].sort((a, b) =>
        String(a.strategy || a.engine || "").localeCompare(String(b.strategy || b.engine || ""))
      );
    } else {
      list = [...list].sort((a, b) => {
        const ta = new Date(a.closed_at || a.exit_time || a.entry_time || 0).getTime();
        const tb = new Date(b.closed_at || b.exit_time || b.entry_time || 0).getTime();
        return tb - ta;
      });
    }
    return list;
  }, [sourceTrades, accountFilter, engineFilter, sortBy]);

  const handleTradeClick = (trade, isOpenTrade) => {
    setSelectedTrade({ trade, isOpen: isOpenTrade });
  };

  const pnlWindowTotal = pnlBuckets.reduce((s, b) => s + b.pnl, 0);
  const grossExposure = positions.reduce((sum, position) => {
    const explicitSize = Number(position.position_size_usd ?? position.notional ?? position.size_usd);
    if (Number.isFinite(explicitSize) && explicitSize) return sum + Math.abs(explicitSize);
    const margin = Number(position.margin || 0);
    const leverage = Number(position.leverage || 1);
    return sum + Math.abs(margin * leverage);
  }, 0);
  const weexValidationStats = positions.reduce((stats, position) => {
    const status = weexMetaFor(position).status;
    if (["MARKET_VALIDATED", "DEMO_VALIDATED"].includes(status)) stats.validated += 1;
    if (status === "DEMO_VALIDATED") stats.demo += 1;
    if (["DRIFT", "DEMO_DRIFT", "DEMO_REJECTED"].includes(status)) stats.attention += 1;
    if (status === "SYNCING") stats.syncing += 1;
    return stats;
  }, { validated: 0, demo: 0, attention: 0, syncing: 0 });
  const weexSummary = positionsLoading && !lastUpdated
    ? "WEEX validation syncing"
    : !weexShadow?.market_feed_online
      ? "WEEX validation feed unavailable"
      : `${weexValidationStats.validated}/${positions.length} WEEX validated`;
  const totalAccountBalance = accounts.reduce((sum, account) => sum + Number(account.balance || 0), 0);
  const totalMarginInUse = accounts.reduce((sum, account) => sum + Number(account.total_margin_in_use || 0), 0);
  const normalizedHeat = totalAccountBalance > 0 ? (totalMarginInUse / totalAccountBalance) * 100 : 0;
  const filteredPositions = accountFilter === "ALL"
    ? positions
    : positions.filter(position => String(position.account_id || position.account || "") === accountFilter);
  const filteredWeexValidated = filteredPositions.filter((position) =>
    ["MARKET_VALIDATED", "DEMO_VALIDATED"].includes(weexMetaFor(position).status)
  ).length;
  const initialSync = positionsLoading && !lastUpdated;
  const pulseState = String(systemPulse?.overall || "").toLowerCase();
  const hasDeskErrors = deskErrors.length > 0;
  const executionTone = positionError || hasDeskErrors
    ? "degraded"
    : initialSync
      ? "syncing"
      : ["down", "crisis"].includes(pulseState)
        ? "down"
        : ["low_health", "degraded"].includes(pulseState)
          ? "warning"
          : "live";
  const executionLabel = {
    degraded: "Data degraded",
    syncing: "Synchronizing",
    down: "Execution unavailable",
    warning: "Low system health",
    live: "Data synchronized",
  }[executionTone];
  const executionColor = {
    degraded: "#ff8a94",
    syncing: "#f5b042",
    down: "#ff5d6c",
    warning: "#f5b042",
    live: "#4ade80",
  }[executionTone];
  const statusDetail = positionError
    ? positionError
    : hasDeskErrors
      ? `Unavailable: ${deskErrors.join(", ")}`
      : lastUpdated
        ? `OKX marks synced ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
        : "Connecting to market and execution data";
  const marketChartNode = typeof marketChart === "function" ? marketChart(positions) : marketChart;
  const deskViews = [
    { id: "live", label: "Live Desk", detail: "Markets & open risk", icon: Crosshair },
    { id: "performance", label: "Performance", detail: "Returns & attribution", icon: Activity },
    { id: "risk", label: "Risk & Accounts", detail: "Capital controls", icon: ShieldCheck },
    { id: "ledger", label: "Trade Ledger", detail: "Execution history", icon: History },
  ];

  // ── Markets redesign: shared header + four views ─────────────────────────
  const tz = systemPulse?.operator_tz || (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
  })();
  const activeIds = useMemo(() => new Set(Object.keys(portfolioSummary?.accounts || {})), [portfolioSummary]);
  const activeTrades = useMemo(
    () => visibleTrades.filter(t => activeIds.has(t.account_id || t.account || "")),
    [visibleTrades, activeIds],
  );
  const activeWin = useMemo(() => {
    const wins = activeTrades.filter(t => Number(t.pnl_pct ?? 0) > 0).length;
    const losses = activeTrades.filter(t => Number(t.pnl_pct ?? 0) < 0).length;
    return { rate: wins + losses ? (wins / (wins + losses)) * 100 : null, n: activeTrades.length };
  }, [activeTrades]);
  const exposure = positions.reduce((sum, p) => sum + notional(p), 0);
  const posAccounts = new Set(positions.map(p => p.account_id)).size;
  const headerMetrics = [
    { label: "30-day result", scope: `realized · ${ACTIVE_SCOPE}`, value: equity.length ? money(totalPnl) : "—", tone: tone(totalPnl),
      title: "Sum of realized P&L on trades closed in the last 30 days, LAB + PERSONAL + REAL_LIFE + RESEARCH" },
    { label: "Win rate", scope: `${fmtCount(activeWin.n)} trades, all time`, value: activeWin.rate == null ? "—" : fmtPct(activeWin.rate),
      title: `Winning ÷ graded closed trades, ${ACTIVE_SCOPE}, all time` },
    { label: "Gross exposure", scope: "open notional", value: initialSync ? "—" : money(exposure, { sign: false, dec: 0 }) },
    { label: "Portfolio heat", scope: "balance-weighted", value: fmtPct(normalizedHeat),
      tone: normalizedHeat > 70 ? "is-down" : "",
      title: "Total margin in use ÷ total balance across accounts. Per-account heat is in Portfolio risk." },
    { label: "Drawdown", scope: `from 30-day peak ${equity.length ? money(peak, { dec: 0 }) : ""}`, value: equity.length ? money(drawdown) : "—",
      tone: drawdown < 0 ? "is-down" : "", title: "30-day realized P&L now, minus its highest point in the window" },
    { label: "Open positions", scope: `${posAccounts} account${posAccounts === 1 ? "" : "s"}`, value: initialSync ? "—" : fmtCount(positions.length) },
  ];
  const session = { loading: initialSync, realized: todayRealized, open: openUnrealized, total: sessionLivePnl };
  const openRow = (row, isOpenRow) => (isOpenRow ? setSelectedPos(row) : setSelectedTrade({ trade: row, isOpen: false }));

  return (
    <MarketsCtx.Provider value={{ tz }}>
      <div className="mk">
        <MarketsHeader
          view={workspaceView}
          onView={setWorkspaceView}
          session={session}
          metrics={headerMetrics}
          pulse={systemPulse}
          onRefresh={refreshDesk}
          refreshing={false}
          filter={accountFilter}
          onClearFilter={() => setAccountFilter("ALL")}
        />
        {hasDeskErrors || positionError ? (
          <p className="mk-status-line is-warn" role="status">
            {positionError ? `Position feed: ${positionError}. ` : ""}{hasDeskErrors ? `Unavailable: ${deskErrors.join(", ")}.` : ""}
          </p>
        ) : null}

        {workspaceView === "live" && (
          <LiveDesk
            positions={filteredPositions}
            positionsLoading={initialSync}
            positionError={positionError}
            weexShadow={weexShadow}
            activeTrades={activeTrades}
            onOpenPosition={setSelectedPos}
          />
        )}

        {workspaceView === "performance" && (
          <Performance equity={equity} activeTrades={activeTrades} />
        )}

        {workspaceView === "risk" && (
          <RiskAccounts
            accounts={accounts}
            trades={activeTrades}
            heatOk={normalizedHeat <= 70}
            filter={accountFilter}
            onFilter={setAccountFilter}
            onDetail={openDetailModal}
          />
        )}

        {workspaceView === "ledger" && (
          <TradeLedger
            openRows={positions}
            closedRows={visibleTrades}
            accountFilter={accountFilter}
            onAccountFilter={setAccountFilter}
            onOpenRow={openRow}
          />
        )}
      </div>

      <div className="mk-modal-scope">
      {/* Modals */}
      {selectedPos && (
        <CallingCardModal
          pos={selectedPos}
          onClose={() => setSelectedPos(null)}
          onClosePosition={closePosition}
          actionError={positionActionError}
        />
      )}
      {selectedTrade && !selectedTrade.isOpen && (
        <TradeModal
          trade={selectedTrade.trade}
          isOpen={false}
          onClose={() => setSelectedTrade(null)}
        />
      )}

      {/* ── Account Detail Modal ──────────────────────────────────────────── */}
      {detailAccount && (() => {
        const acc   = detailAccount;
        const meta  = ACCOUNT_META[acc.id] || { color: "#5cf2ff", role: acc.id };
        const color = meta.color;
        const d     = detailData;
        const st    = d?.stats || {};
        const tabs  = [
          { id: "overview", label: "Overview" },
          { id: "open",     label: `Open${d ? ` (${d.total_open})` : ""}` },
          { id: "history",  label: `History${d ? ` (${d.total_closed})` : ""}` },
        ];
        const fmtDur = (min) => {
          if (!min && min !== 0) return "";
          if (min < 60) return `${min}m`;
          if (min < 1440) return `${Math.floor(min/60)}h ${min%60}m`;
          return `${Math.floor(min/1440)}d ${Math.floor((min%1440)/60)}h`;
        };
        const fmtP = (n) => {
          if (!n && n !== 0) return "—";
          const abs = Math.abs(n);
          return abs > 1000 ? n.toLocaleString(undefined,{maximumFractionDigits:0})
               : abs > 1    ? n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})
               :               n.toLocaleString(undefined,{minimumFractionDigits:4,maximumFractionDigits:4});
        };

        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: "rgba(0,0,0,0.82)", backdropFilter: "blur(8px)" }}
            onClick={() => setDetailAccount(null)}>
            <div className="w-full sm:max-w-xl max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden"
              style={{ background: "#0a0a0f", border: `1px solid ${color}22` }}
              onClick={e => e.stopPropagation()}>

              {/* ── Header ── */}
              <div className="px-5 pt-5 pb-4 flex-shrink-0" style={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <ProgressRing pct={Math.round(d?.stats?.win_rate || accountWinRates[acc.id] || 0)} size={44} stroke={3} color={color}>
                      <span className="text-[9px] font-mono font-bold" style={{ color }}>{Math.round(d?.stats?.win_rate || accountWinRates[acc.id] || 0)}</span>
                    </ProgressRing>
                    <div>
                      <div className="font-display text-base font-bold uppercase tracking-[0.2em]" style={{ color }}>{acc.id}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">{meta.role}</div>
                    </div>
                  </div>
                  <button onClick={() => setDetailAccount(null)} className="w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)" }}>
                    <X className="w-4 h-4 text-white/50" />
                  </button>
                </div>

                {/* Top stat strip */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: "Balance",  val: fmtUsd(d?.account?.balance ?? acc.balance ?? 0, 0), sub: null },
                    { label: "Total PnL", val: `${(d?.account?.total_pnl ?? 0) >= 0 ? "+" : ""}${fmtUsd(d?.account?.total_pnl ?? 0)}`,
                      tone: (d?.account?.total_pnl ?? 0) >= 0 ? "#4ade80" : "#ff5d6c" },
                    { label: "Growth",   val: `${(d?.account?.growth_pct ?? 0) >= 0 ? "+" : ""}${d?.account?.growth_pct ?? 0}%`,
                      tone: (d?.account?.growth_pct ?? 0) >= 0 ? "#4ade80" : "#ff5d6c" },
                    { label: "Trades",   val: d?.account?.total_trades ?? acc.total_trades ?? 0, sub: null },
                  ].map(({ label, val, tone, sub }) => (
                    <div key={label} className="rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div className="text-[9px] uppercase tracking-[0.22em] text-white/35 mb-1">{label}</div>
                      <div className="font-mono text-sm font-semibold" style={{ color: tone || "white" }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-3">
                  {tabs.map(tab => (
                    <button key={tab.id} type="button"
                      onClick={() => setDetailTab(tab.id)}
                      className="flex-1 py-2 rounded-xl text-[10px] uppercase tracking-[0.2em] font-semibold transition-all"
                      style={detailTab === tab.id
                        ? { background: `${color}22`, color, border: `1px solid ${color}44` }
                        : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Tab body ── */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {detailLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-6 h-6 rounded-full border-2 animate-spin"
                      style={{ borderColor: color, borderTopColor: "transparent" }} />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/30">Loading account data…</span>
                  </div>
                ) : !d ? (
                  <p className="text-[10px] text-white/25 text-center py-8">Failed to load</p>
                ) : detailTab === "overview" ? (
                  <div className="space-y-5">
                    {/* Performance metrics */}
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">Performance</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "Profit Factor", val: st.profit_factor >= 99 ? "∞" : st.profit_factor?.toFixed(2), tone: st.profit_factor >= 1 ? "#4ade80" : "#ff5d6c" },
                          { label: "Avg R:R",        val: st.avg_rr?.toFixed(2),   tone: st.avg_rr >= 1 ? "#4ade80" : "#f5b042" },
                          { label: "Win Rate",       val: `${st.win_rate}%`,        tone: st.win_rate >= 50 ? "#4ade80" : "#f5b042" },
                          { label: "Avg Win",        val: money(st.avg_win),  tone: "#4ade80" },
                          { label: "Avg Loss",       val: money(st.avg_loss),  tone: "#ff5d6c" },
                          { label: "W / L",          val: `${d.account.wins} / ${d.account.losses}`, tone: "white" },
                        ].map(({ label, val, tone }) => (
                          <div key={label} className="rounded-xl px-3 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="text-[8px] uppercase tracking-[0.22em] text-white/30 mb-1">{label}</div>
                            <div className="font-mono text-sm font-bold" style={{ color: tone }}>{val}</div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.12)" }}>
                          <div className="text-[8px] uppercase tracking-[0.22em] text-white/30 mb-1">Gross Profit</div>
                          <div className="font-mono text-sm font-bold text-emerald-400">{money(st.gross_profit)}</div>
                        </div>
                        <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(255,93,108,0.05)", border: "1px solid rgba(255,93,108,0.12)" }}>
                          <div className="text-[8px] uppercase tracking-[0.22em] text-white/30 mb-1">Gross Loss</div>
                          <div className="font-mono text-sm font-bold text-rose-400">{money(-Math.abs(st.gross_loss || 0))}</div>
                        </div>
                      </div>
                    </div>

                    {/* Daily PnL bars */}
                    {Object.keys(d.daily_pnl || {}).length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">Daily PnL (14d)</div>
                        <div className="flex items-end gap-1 h-14">
                          {Object.entries(d.daily_pnl).sort(([a],[b])=>a.localeCompare(b)).map(([day, pnl]) => {
                            const maxAbs = Math.max(...Object.values(d.daily_pnl).map(Math.abs), 1);
                            const h = Math.round(Math.abs(pnl) / maxAbs * 100);
                            return (
                              <div key={day} className="flex-1 flex flex-col items-center gap-0.5 group">
                                <div className="w-full rounded-sm transition-all" title={`${day}: $${pnl}`}
                                  style={{ height: `${Math.max(h, 4)}%`, background: pnl >= 0 ? "rgba(74,222,128,0.6)" : "rgba(255,93,108,0.6)", minHeight: 2 }} />
                                <div className="text-[7px] text-white/20 group-hover:text-white/60">{day.slice(3)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* By engine */}
                    {d.by_engine?.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">By Engine</div>
                        <div className="space-y-1.5">
                          {d.by_engine.map(e => (
                            <div key={e.engine} className="flex items-center gap-3 px-3 py-2 rounded-xl"
                              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-semibold text-white/80 truncate">{e.engine}</div>
                                <div className="text-[9px] text-white/30">{e.count} trades · {e.win_rate}% WR</div>
                              </div>
                              <div className={`font-mono text-sm font-bold ${e.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                {e.pnl >= 0 ? "+" : ""}{fmtUsd(e.pnl)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* By direction */}
                    {d.by_direction?.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">Long vs Short</div>
                        <div className="grid grid-cols-2 gap-2">
                          {d.by_direction.map(dir => (
                            <div key={dir.direction} className="rounded-xl px-3 py-2.5"
                              style={{ background: dir.direction === "LONG" ? "rgba(74,222,128,0.05)" : "rgba(255,93,108,0.05)",
                                border: dir.direction === "LONG" ? "1px solid rgba(74,222,128,0.12)" : "1px solid rgba(255,93,108,0.12)" }}>
                              <div className="flex justify-between mb-1">
                                <span className={`text-[9px] font-bold uppercase ${dir.direction === "LONG" ? "text-emerald-400" : "text-rose-400"}`}>{dir.direction}</span>
                                <span className="text-[9px] text-white/30">{dir.win_rate}% WR</span>
                              </div>
                              <div className={`font-mono text-base font-bold ${dir.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                {dir.pnl >= 0 ? "+" : ""}{fmtUsd(dir.pnl)}
                              </div>
                              <div className="text-[9px] text-white/30">{dir.count} trades</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Best / worst */}
                    {(st.best_trade || st.worst_trade) && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">Best & Worst</div>
                        <div className="grid grid-cols-2 gap-2">
                          {[{ label: "Best Trade", t: st.best_trade, tone: "emerald" }, { label: "Worst Trade", t: st.worst_trade, tone: "rose" }].map(({ label, t, tone }) => t && (
                            <div key={label} className="rounded-xl px-3 py-2.5"
                              style={{ background: `rgba(${tone === "emerald" ? "74,222,128" : "255,93,108"},0.05)`, border: `1px solid rgba(${tone === "emerald" ? "74,222,128" : "255,93,108"},0.12)` }}>
                              <div className="text-[8px] uppercase tracking-[0.2em] text-white/30 mb-1">{label}</div>
                              <div className="font-semibold text-white text-sm">{t.symbol?.replace("/USDT","")}</div>
                              <div className={`font-mono text-sm font-bold ${tone === "emerald" ? "text-emerald-400" : "text-rose-400"}`}>
                                {(t.realized_pnl||0) >= 0 ? "+" : ""}{fmtUsd(t.realized_pnl||0)}
                              </div>
                              <div className="text-[9px] text-white/30">{t.direction} · {t.leverage}x · {(t.close_reason||"").replace("_"," ")}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Close reasons */}
                    {d.by_close_reason?.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">Close Reasons</div>
                        <div className="space-y-1">
                          {d.by_close_reason.map(r => (
                            <div key={r.reason} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                              style={{ background: "rgba(255,255,255,0.025)" }}>
                              <div className="flex-1">
                                <span className="text-[10px] text-white/60">{r.reason.replace(/_/g," ")}</span>
                                <span className="text-[9px] text-white/25 ml-2">{r.count}×</span>
                              </div>
                              <span className={`font-mono text-xs font-semibold ${r.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                {r.pnl >= 0 ? "+" : ""}{fmtUsd(r.pnl)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                ) : detailTab === "open" ? (
                  <div className="space-y-3">
                    {d.open?.length === 0 && <p className="text-[10px] text-white/25 text-center py-8">No open positions</p>}
                    {d.open?.map((t, i) => {
                      const pnl = t.unrealized_pnl || 0;
                      const pct = t.unrealized_pnl_pct || 0;
                      const isLong = t.direction === "LONG";
                      const openedDate = t.opened_at ? new Date(t.opened_at) : null;
                      const ageMin = openedDate ? Math.round((Date.now() - openedDate.getTime()) / 60000) : null;
                      return (
                        <div key={i} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${pnl >= 0 ? "rgba(74,222,128,0.15)" : "rgba(255,93,108,0.15)"}` }}>
                          {/* Row 1: symbol + direction + leverage + PnL */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${isLong ? "text-emerald-300 bg-emerald-400/15" : "text-rose-300 bg-rose-400/15"}`}>{t.direction}</span>
                              <span className="font-display text-lg font-bold text-white">{t.symbol?.replace("/USDT","")}</span>
                              <span className="text-xs text-white/40">{t.leverage}x</span>
                            </div>
                            <div className="text-right">
                              <div className={`font-mono text-base font-bold ${pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                {money(pnl)}
                              </div>
                              <div className={`text-[10px] ${pct >= 0 ? "text-emerald-400/70" : "text-rose-400/70"}`}>{fmtPct(pct, { sign: true, dec: 2 })}</div>
                            </div>
                          </div>
                          {/* Row 2: price grid */}
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            {[
                              { label: "Entry",   val: `$${fmtP(t.entry_price)}` },
                              { label: "Current", val: `$${fmtP(t.current_price)}`, bold: true },
                              { label: "Liq",     val: `$${fmtP(t.liquidation_price)}`, tone: "#ff5d6c" },
                            ].map(({label, val, tone, bold}) => (
                              <div key={label} className="rounded-lg px-2.5 py-2" style={{ background: "rgba(255,255,255,0.04)" }}>
                                <div className="text-[8px] uppercase tracking-[0.2em] text-white/30 mb-0.5">{label}</div>
                                <div className="font-mono text-xs font-semibold" style={{ color: tone || "white", fontWeight: bold ? 700 : 600 }}>{val}</div>
                              </div>
                            ))}
                          </div>
                          {/* Row 3: SL / TP / Margin / Size */}
                          <div className="grid grid-cols-4 gap-2 mb-3">
                            {[
                              { label: "Stop Loss",   val: `$${fmtP(t.stop_loss)}`,     tone: "#ff5d6c" },
                              { label: "Take Profit", val: `$${fmtP(t.take_profit)}`,   tone: "#4ade80" },
                              { label: "Margin",      val: `$${fmtP(t.margin)}` },
                              { label: "Size",        val: `$${fmtP(t.position_size_usd)}` },
                            ].map(({label, val, tone}) => (
                              <div key={label} className="rounded-lg px-2 py-1.5" style={{ background: "rgba(255,255,255,0.03)" }}>
                                <div className="text-[7px] uppercase tracking-[0.18em] text-white/25 mb-0.5">{label}</div>
                                <div className="font-mono text-[11px] font-semibold" style={{ color: tone || "rgba(255,255,255,0.7)" }}>{val}</div>
                              </div>
                            ))}
                          </div>
                          {/* Row 4: TP stages + trail + engine + age */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {[
                                { label: "TP1", hit: t.tp1_hit },
                                { label: "TP2", hit: t.tp2_hit },
                                { label: "Trail", hit: t.trail_active },
                              ].map(({label, hit}) => (
                                <span key={label} className="text-[9px] px-2 py-0.5 rounded-full font-semibold"
                                  style={{ background: hit ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.06)", color: hit ? "#4ade80" : "rgba(255,255,255,0.25)" }}>
                                  {label} {hit ? "✓" : "–"}
                                </span>
                              ))}
                            </div>
                            <div className="text-right">
                              <div className="text-[9px] text-white/40">{t.strategy}</div>
                              {ageMin !== null && <div className="text-[9px] text-white/25">{fmtDur(ageMin)} ago</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                ) : (
                  <div className="space-y-2">
                    {d.closed?.length === 0 && <p className="text-[10px] text-white/25 text-center py-8">No closed trades yet</p>}
                    {d.closed?.map((t, i) => {
                      const pnl = t.realized_pnl || 0;
                      const win = pnl > 0;
                      const isLong = t.direction === "LONG";
                      return (
                        <div key={i} className="rounded-xl p-3.5" style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${win ? "rgba(74,222,128,0.08)" : "rgba(255,93,108,0.08)"}` }}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${isLong ? "text-emerald-300 bg-emerald-400/10" : "text-rose-300 bg-rose-400/10"}`}>{t.direction}</span>
                              <span className="font-semibold text-white">{t.symbol?.replace("/USDT","")}</span>
                              <span className="text-[10px] text-white/30">{t.leverage}x</span>
                              {t.tp1_hit && <span className="text-[8px] text-emerald-400/60">TP1✓</span>}
                              {t.tp2_hit && <span className="text-[8px] text-emerald-400/60">TP2✓</span>}
                            </div>
                            <div className={`font-mono text-sm font-bold ${win ? "text-emerald-400" : "text-rose-400"}`}>
                              {money(pnl)}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-[9px] text-white/35">
                            <div className="flex items-center gap-1.5">
                              <span>${fmtP(t.entry_price)}</span>
                              <span className="text-white/20">→</span>
                              <span>${fmtP(t.exit_price)}</span>
                              <span className="text-white/20">·</span>
                              <span className="capitalize">{(t.close_reason||"").replace(/_/g," ")}</span>
                            </div>
                            <div className="flex items-center gap-2 text-right">
                              <span className="text-white/20">{t.duration_min ? fmtDur(t.duration_min) : ""}</span>
                              <span>{t.closed_at ? new Date(t.closed_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : ""}</span>
                            </div>
                          </div>
                          <div className="mt-1 text-[9px] text-white/20">{t.strategy} · margin ${fmtP(t.margin)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </MarketsCtx.Provider>
  );
}
